import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Music, Trophy, Info, BarChart2, X, ChevronRight, Loader2, ArrowRightLeft, Undo2, RotateCcw, Eye, EyeOff, Check, Share2, Lightbulb, Sparkles, MessageSquare, Send, Users, Flame, ShieldCheck, User, UserCheck, Star } from 'lucide-react';
import { BrowserRouter, Routes, Route, useParams, useNavigate, Navigate, useLocation, Link } from 'react-router-dom';
import { cn } from './lib/utils';
import { Track, GameStep, DailyChallenge } from './types';
import { supabase, syncCloudStats } from './supabase';
import { safeStorage } from './lib/safeStorage';
import { CalendarModal } from './components/CalendarModal';
import { CustomChallengeModal } from './components/CustomChallengeModal';
import { AuthModal } from './components/AuthModal';
import { UpdateNoticeModal } from './components/UpdateNoticeModal';
import { ChallengeNoticeModal } from './components/ChallengeNoticeModal';
import { NotFound } from './components/NotFound';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfService from './TermsOfService';

const proxySpotifyUrl = (url: string | null | undefined) => {
  if (!url) return url;
  if (url.startsWith('/api/proxyImage')) return url;
  if (url.includes('i.scdn.co') || url.includes('spotifycdn.com')) {
    return `/api/proxyImage?url=${encodeURIComponent(url)}`;
  }
  return url;
};

const formatArtistName = (name: string) => {
  if (!name) return name;
  const upper = name.toUpperCase();
  if (upper === 'J-HOPE') return 'j-hope';
  if (upper === 'ROSE' || upper === 'ROSÉ') return 'ROSÉ';
  if (upper === 'BENNY BLANCO' || upper === 'BEENY BLANCO') return 'benny blanco';
  if (upper === 'BEABADOOBEE') return 'beabadoobee';
  if (upper === 'BLACKBEAR') return 'blackbear';
  if (upper === 'BLINK-182') return 'blink-182';
  if (upper === 'WILL.I.AM') return 'will.i.am';
  if (upper === 'MGK') return 'mgk';
  if (upper === 'KAROL G') return 'KAROL G';
  if (upper === 'LISA') return 'LISA';
  if (upper === 'GIVEON') return 'GIVEON';
  if (upper === 'P!NK') return 'P!nk';
  if (upper === 'KATSEYE') return 'KATSEYE';
  const allCapsArtists = [
    'RAYE', 'JENNIE', 'SZA', 'KAROL G', 'ROSÉ', 'WILLOW', 'BLACKPINK',
    'KAYTRANADA', 'SEVENTEEN', 'TWICE', 'GIVEON', 'H.E.R.', 'BADBADNOTGOOD', 'ZAYN',
    'LISA', 'JISOO', 'KATSEYE', 'AUDREY NUNA', 'REI AMI', 'LE SSERAFIM', 'ITZY', 
    'IVE', 'ENHYPEN', 'FIFTY FIFTY', 'RIIZE', 'KYLE'
  ];
  if (allCapsArtists.includes(upper)) {
    return upper;
  }
  return name;
};

function TrackImage({ track, className }: { track: Track; className?: string }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const images = track.album?.images || [];
  // Use a medium sized image if available, otherwise smallest
  const rawImageUrl = images.length > 1 ? images[1].url : (images[0]?.url || null);
  const imageUrl = proxySpotifyUrl(rawImageUrl);

  useEffect(() => {
    if (imgRef.current?.complete) {
      setIsLoaded(true);
    }
  }, [imageUrl]);

  return (
    <div className={cn("w-12 h-12 bg-[#1c1c1c] rounded-lg flex-shrink-0 overflow-hidden shadow-lg relative flex items-center justify-center", className)}>
      {!isLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#2a2a2a] text-[10px] font-black text-gray-600 text-center p-1 leading-tight uppercase tracking-tighter">
          {track.name.split(' ').slice(0, 2).map((word, idx) => (
            <div key={`${word}-${idx}`}>{word.substring(0, 8)}</div>
          ))}
        </div>
      )}
      {imageUrl && (
        <img 
          ref={imgRef}
          key={imageUrl}
          src={imageUrl} 
          alt={track.name} 
          className={cn(
            "w-full h-full object-cover transition-opacity duration-500",
            isLoaded ? "opacity-100" : "opacity-0"
          )}
          onLoad={() => setIsLoaded(true)}
          referrerPolicy="no-referrer"
        />
      )}
    </div>
  );
}

function ArtistImage({ src, alt, className }: { src: string; alt: string; className?: string }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [fallbackSrc, setFallbackSrc] = useState<string | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const proxiedSrc = proxySpotifyUrl(src);

  useEffect(() => {
    setIsLoaded(false);
    setHasError(false);
    setFallbackSrc(null);
  }, [src]);

  useEffect(() => {
    if (imgRef.current?.complete) {
      setIsLoaded(true);
    }
  }, [proxiedSrc, fallbackSrc]);

  const initials = alt.split(' ').slice(0, 2).map(n => n[0]).join('').toUpperCase();

  const handleError = () => {
    if (!fallbackSrc && src && !src.includes('picsum.photos')) {
      // Try picsum as a second fallback if the primary image fails
      setFallbackSrc(`https://picsum.photos/seed/${encodeURIComponent(alt)}/200/200`);
    } else {
      setHasError(true);
    }
  };

  const displaySrc = fallbackSrc || proxiedSrc;

  return (
    <div className={cn("relative flex items-center justify-center bg-[#1c1c1c] overflow-hidden", className)}>
      {!isLoaded && !hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#2a2a2a] animate-pulse">
          <Music className="w-1/2 h-1/2 text-gray-700" />
        </div>
      )}
      {(hasError || !displaySrc || displaySrc === '/img/default.png') ? (
        <div className="absolute inset-0 flex items-center justify-center bg-[#2a2a2a] text-gray-600 font-black text-2xl">
          {initials || <Music className="w-1/2 h-1/2" />}
        </div>
      ) : (
        <img 
          ref={imgRef}
          key={displaySrc}
          src={displaySrc} 
          alt={alt} 
          className={cn(
            "w-full h-full object-cover transition-opacity duration-500",
            isLoaded ? "opacity-100" : "opacity-0"
          )}
          referrerPolicy="no-referrer"
          onLoad={() => setIsLoaded(true)}
          onError={handleError}
        />
      )}
    </div>
  );
}

function OracleSolutionSection({ 
  oracleSolution, 
  isFetchingSolution, 
  revealOraclePath, 
  setRevealOraclePath,
  isLightMode,
  challenge,
  isModal = false
}: { 
  oracleSolution: { path: any[], steps: number } | null;
  isFetchingSolution: boolean;
  revealOraclePath: boolean;
  setRevealOraclePath: (reveal: boolean) => void;
  isLightMode: boolean;
  challenge: DailyChallenge | null;
  isModal?: boolean;
}) {
  const handleReveal = () => {
    const newReveal = !revealOraclePath;
    setRevealOraclePath(newReveal);

    // Track oracle reveal in analytics ONLY when revealing (going from hidden to shown)
    if (newReveal && challenge) {
      if (typeof (window as any).sa_event === 'function') {
        (window as any).sa_event('oracle_solution_revealed', { date: challenge.date });
      }
      if (typeof (window as any).umami?.track === 'function') {
        (window as any).umami.track('oracle_solution_revealed', { date: challenge.date });
      }
    }
  };

  return (
    <div className={cn(
      "mb-8 p-4 rounded-2xl border text-left transition-all duration-300",
      isLightMode 
        ? "bg-[#ece9e4] border-black/5 shadow-inner" 
        : "bg-black/30 border-white/5"
    )}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[10px] uppercase tracking-widest font-black text-gray-500">The Oracle Says</h3>
        {isFetchingSolution ? (
          <Loader2 className="w-3 h-3 animate-spin text-[#ff0080]" />
        ) : oracleSolution ? (
          <button 
            onClick={handleReveal}
            className="flex items-center gap-1.5 text-[10px] font-bold text-[#ff0080] hover:opacity-80 transition-opacity"
          >
            Shortest: {oracleSolution.steps} {oracleSolution.steps === 1 ? 'step' : 'steps'}
            {revealOraclePath ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
          </button>
        ) : null}
      </div>
      
      <AnimatePresence mode="wait">
        {oracleSolution ? (
          revealOraclePath ? (
            <motion.div 
              key="oracle-path"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-0 overflow-hidden"
            >
              {oracleSolution.path.map((step, i) => (
                <React.Fragment key={i}>
                  {/* Artist Node */}
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-[#ff0080] shrink-0 shadow-[0_0_8px_rgba(255,0,128,0.5)]" />
                    <p className={cn("text-xs font-bold truncate transition-colors", isLightMode ? "text-[#1a1a1a]" : "text-white")}>
                      {step.spotifyId ? (
                        <a 
                          href={`https://open.spotify.com/artist/${step.spotifyId}`} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="hover:text-[#ff0080] transition-colors"
                        >
                          {formatArtistName(step.name)}
                        </a>
                      ) : formatArtistName(step.name)}
                    </p>
                  </div>

                  {/* Connecting Track (if not last) */}
                  {i < oracleSolution.path.length - 1 && (
                    <div className="flex items-stretch gap-2 min-h-[36px]">
                      <div className="w-2 flex flex-col items-center shrink-0">
                        <div className="flex-1 w-px bg-[#ff0080]/40" />
                        <div className="w-0 h-0 border-l-[2px] border-l-transparent border-r-[2px] border-r-transparent border-t-[3px] border-t-[#ff0080]/40" />
                      </div>
                      <div className="flex items-center min-w-0 py-1">
                        <p className={cn(
                          "text-[10.5px] leading-relaxed text-gray-500 italic pr-1",
                          isModal ? "line-clamp-2 max-w-[280px]" : "break-words"
                        )}>
                          via {formatArtistName(step.primaryArtist || step.name)} — {step.trackId ? (
                            <a 
                              href={`https://open.spotify.com/track/${step.trackId}`} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="hover:text-[#ff0080] transition-colors pr-1 inline-block"
                            >
                              {step.track}
                            </a>
                          ) : <span className="pr-1 inline-block">{step.track}</span>}
                        </p>
                      </div>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </motion.div>
          ) : (
            <motion.p 
              key="oracle-prompt"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="text-[10px] text-gray-500 italic"
            >
              Click the eye to reveal the shortest path...
            </motion.p>
          )
        ) : (
          <p className="text-[10px] text-gray-500 italic">The Oracle is still learning this path...</p>
        )}
      </AnimatePresence>
    </div>
  );
}

export const fetchWithTimeout = async (url: string, options: any = {}, timeout = 30000, maxRetries = 5) => {
  let attempt = 0;
  
  while (attempt < maxRetries) {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);
    
    const onAbort = () => controller.abort();
    if (options.signal) {
      if (options.signal.aborted) {
        controller.abort();
      } else {
        options.signal.addEventListener('abort', onAbort);
      }
    }

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });
      clearTimeout(id);
      if (options.signal) options.signal.removeEventListener('abort', onAbort);
      
      const contentType = response.headers.get("content-type");
      if (url.startsWith('/api/') && contentType && contentType.includes('text/html')) {
        throw new Error("Dev server is starting (received HTML for API route)");
      }
      
      return response;
    } catch (error: any) {
      clearTimeout(id);
      if (options.signal) options.signal.removeEventListener('abort', onAbort);
      
      if (error.message === "Dev server is starting (received HTML for API route)" && attempt < maxRetries - 1) {
        attempt++;
        await new Promise(resolve => setTimeout(resolve, 3000));
        continue;
      }
      throw error;
    }
  }
  throw new Error("Max retries exceeded");
};

function GameContent() {
  const { date, code } = useParams<{ date?: string; code?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [gameState, setGameState] = useState<'landing' | 'playing' | 'won'>('landing');
  const [challenge, setChallenge] = useState<DailyChallenge | null>(null);
  const [currentArtist, setCurrentArtist] = useState<string>('');
  const [currentArtistId, setCurrentArtistId] = useState<string>('');
  const [targetArtist, setTargetArtist] = useState<string>('');
  const [targetArtistId, setTargetArtistId] = useState<string>('');
  const [currentArtistImg, setCurrentArtistImg] = useState<string>('');
  const [targetArtistImg, setTargetArtistImg] = useState<string>('');
  const [sessionStartArtist, setSessionStartArtist] = useState<string>('');
  const [sessionStartArtistId, setSessionStartArtistId] = useState<string>('');
  const [challengeStartArtistImg, setChallengeStartArtistImg] = useState<string>('');
  const [challengeTargetArtistImg, setChallengeTargetArtistImg] = useState<string>('');
  const [chain, setChain] = useState<GameStep[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Track[]>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [featuredOptions, setFeaturedOptions] = useState<{ trackId: string; trackName: string; artists: { id: string; name: string }[]; isHint?: boolean } | null>(null);
  const [nextChallengeSeconds, setNextChallengeSeconds] = useState<number>(0);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showCustomChallengeModal, setShowCustomChallengeModal] = useState(false);
  const [oracleSolution, setOracleSolution] = useState<{ path: any[], steps: number } | null>(null);
  const [isFetchingSolution, setIsFetchingSolution] = useState(false);
  const [revealOraclePath, setRevealOraclePath] = useState(false);
  const [showVictoryModal, setShowVictoryModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [highlightedArtistIndex, setHighlightedArtistIndex] = useState(-1);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const [leaderboardData, setLeaderboardData] = useState<any[]>([]);
  const [isFetchingLeaderboard, setIsFetchingLeaderboard] = useState(false);
  const [isNotFound, setIsNotFound] = useState(false);
  const [notFoundMessage, setNotFoundMessage] = useState("I think you're lost...");
  const [isLightMode, setIsLightMode] = useState(() => safeStorage.getItem('song2song_lightmode') === 'true');
  const [postGameRating, setPostGameRating] = useState<number>(0);
  const [postGameFeedback, setPostGameFeedback] = useState<string>('');
  const [isSubmittingPostGameFeedback, setIsSubmittingPostGameFeedback] = useState(false);
  const [hasSubmittedPostGameFeedback, setHasSubmittedPostGameFeedback] = useState(() => safeStorage.getItem('song2song_feedback_aug2026') === 'true');
  const [user, setUser] = useState<any>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showUpdateNotice, setShowUpdateNotice] = useState(false);
  const [showChallengeNotice, setShowChallengeNotice] = useState(false);

  const toggleLightMode = () => {
    const newValue = !isLightMode;
    setIsLightMode(newValue);
    safeStorage.setItem('song2song_lightmode', String(newValue));
    
    // Analytics
    if (typeof (window as any).sa_event === 'function') {
      (window as any).sa_event('theme_toggled', { theme: newValue ? 'light' : 'dark' });
    }
    if (typeof (window as any).umami?.track === 'function') {
      (window as any).umami.track('theme_toggled', { theme: newValue ? 'light' : 'dark' });
    }
  };

  useEffect(() => {
    const checkChallengeNotice = () => {
      // Show notice for the 2026-09-07 challenge until end of day September 8, 2026
      const cutoffTime = new Date('2026-09-08T23:59:59Z').getTime();
      const now = Date.now();
      
      if (now <= cutoffTime) {
        const hasSeen = safeStorage.getItem('song2song_challenge_notice_20260907_seen');
        if (!hasSeen) {
          setShowChallengeNotice(true);
        }
      }
    };
    
    const timer = setTimeout(checkChallengeNotice, 800);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const checkUpdateNotice = () => {
      // Show notice until August 31, 2026, 11:59 GMT (23:59:59 Z)
      const cutoffTime = new Date('2026-08-31T23:59:59Z').getTime();
      const now = Date.now();
      
      if (now <= cutoffTime) {
        const hasSeen = safeStorage.getItem('song2song_update_notice_seen');
        if (!hasSeen) {
          setShowUpdateNotice(true);
        }
      }
    };
    
    // Slight delay so it doesn't block immediate initial render
    const timer = setTimeout(checkUpdateNotice, 1000);
    return () => clearTimeout(timer);
  }, []);
  
  const [hints, setHints] = useState<Track[]>([]);
  const [isLoadingHints, setIsLoadingHints] = useState(false);
  const [showHints, setShowHints] = useState(false);
  const [hintsUsedToday, setHintsUsedToday] = useState(0);
  const [showHintTooltip, setShowHintTooltip] = useState(false);
  const [hintPulseDismissed, setHintPulseDismissed] = useState(() => safeStorage.getItem('song2songHintPulseDismissed') === 'true');

  useEffect(() => {
    setHints([]);
    setShowHints(false);
  }, [currentArtist]);

  useEffect(() => {
    if (challenge) {
      const savedHints = safeStorage.getItem('song2songHintsUsed') || '{}';
      try {
        const hintsMap = JSON.parse(savedHints);
        const val = hintsMap[challenge.date];
        if (Array.isArray(val)) {
          setHintsUsedToday(val.length);
        } else if (typeof val === 'number') {
          setHintsUsedToday(val);
        } else {
          setHintsUsedToday(0);
        }
      } catch (e) {
        setHintsUsedToday(0);
      }
    }
  }, [challenge]);

  useEffect(() => {
    const hasSeenHintTooltip = safeStorage.getItem('song2songSeenHintTooltip_v3');
    if (!hasSeenHintTooltip && gameState === 'playing') {
      const timer = setTimeout(() => setShowHintTooltip(true), 7000);
      return () => clearTimeout(timer);
    }
  }, [gameState]);

  useEffect(() => {
    if (!supabase) return;

    // Check for OAuth errors in the URL hash (silent failures from Google)
    if (window.location.hash.includes('error_description=')) {
      const hashParams = new URLSearchParams(window.location.hash.replace('#', '?'));
      const errorDesc = hashParams.get('error_description');
      if (errorDesc) {
        alert(`Google Sign-In Failed:\n\n${errorDesc.replace(/\+/g, ' ')}\n\nThis usually means the Client Secret saved in your Supabase Dashboard is incorrect.`);
        // Clean up hash so it doesn't keep alerting on refresh
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    }

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (session?.user) {
        setUser(session.user);
        
        // Only run the heavy sync operation on initial load or fresh login
        // If we run this on USER_UPDATED, it causes an infinite loop because syncCloudStats also updates the user!
        if (event === 'INITIAL_SESSION' || event === 'SIGNED_IN') {
          const merged = await syncCloudStats(session.user, getStats());
          safeStorage.setItem('song2songStats', JSON.stringify(merged));
          
          // Automatically pop the modal if they need to set a username
          if (!session.user.user_metadata?.username) {
            setShowAuthModal(true);
          }
        }
      } else {
        setUser(null);
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const leaderboardTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const analyticsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const searchCache = useRef<Map<string, Track[]>>(new Map());
  const desktopResultsRef = useRef<HTMLDivElement>(null);
  const mobileResultsRef = useRef<HTMLDivElement>(null);

  const [howToImages, setHowToImages] = useState({
    savage: 'https://picsum.photos/seed/21savage/50/50',
    malone: 'https://picsum.photos/seed/postmalone/50/50',
    swift: 'https://picsum.photos/seed/taylorswift/50/50'
  });

  // Visited sets
  const visitedArtists = useRef<Set<string>>(new Set());
  const visitedTracks = useRef<Set<string>>(new Set());

  const normalize = (s: string) => {
    const clean = s.normalize("NFD").toLowerCase().replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9\s]/g, "").trim();
    if (clean === "machine gun kelly" || clean === "machinegun kelly" || clean === "mgk") return "mgk";
    if (clean === "bobby v") return "bobby v";
    return clean;
  };
  const BLACKLISTED_ARTISTS = [
    'THEKINGSBRAWLERS',
    'Marti182',
    'Dolan Beats',
    'ASAPP ROCKY'
  ];

  // Helper to check if a query is a shortcut for an artist
  const isShortcut = (q: string, artist: string, _isTarget: boolean = false) => {
    const normalizedQ = normalize(q);
    const normalizedArtist = normalize(artist);
    
    if (normalizedQ.length < 2) return false;
    
    const qWords = normalizedQ.split(/\s+/).filter(w => w.length > 0);
    const artistWords = normalizedArtist.split(/\s+/).filter(w => w.length > 0);

    // Common words in song titles that should generally NOT be blocked
    const commonWords = [
      'the', 'and', 'for', 'lil', 'some', 'something', 'love', 'life', 'time', 
      'man', 'woman', 'girl', 'boy', 'night', 'day', 'one', 'two', 'all', 
      'new', 'old', 'big', 'small', 'good', 'bad', 'best', 'last', 'first',
      'with', 'feat', 'prod', 'remix', 'edit', 'radio', 'young', 'saint', 'black', 'white',
      'blue', 'red', 'green', 'gold', 'silver', 'star', 'world', 'city', 'town', 'home',
      'we', 'of', 'who', 'what', 'where', 'when', 'why', 'in', 'out', 'up', 'down', 'my', 'your'
    ];
    
    // 1. Check if any word in the query matches or starts an artist name word
    for (const qWord of qWords) {
      if (qWord.length < 2) continue;
      if (commonWords.includes(qWord)) continue;
      
      // Block if query word matches an artist word or is a prefix of one
      if (artistWords.some(aWord => aWord.startsWith(qWord))) return true;
    }

    // 2. Check for substring/prefix matches of the whole query
    // Strict for both current and target
    if (artistWords.some(aWord => aWord.startsWith(normalizedQ))) return true;
    
    // Block if the artist name contains the query as a significant substring 
    if (normalizedQ.length >= 3 && normalizedArtist.includes(normalizedQ)) return true;
    
    return false;
  };

  const chainEndRef = useRef<HTMLDivElement>(null);
  const lastTickRef = useRef<number>(0);

  useEffect(() => {
    if (!supabase) return;

    const channel = supabase
      .channel('public:connections')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'connections' }, (payload) => {
        // Refresh leaderboard and recent connections when a new connection is added
        // Debounce to avoid spamming the server during Oracle expansions
        if (leaderboardTimeoutRef.current) clearTimeout(leaderboardTimeoutRef.current);
        leaderboardTimeoutRef.current = setTimeout(() => {
          fetchLeaderboardData();
        }, 2000);
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('create') === '1') {
      setShowCustomChallengeModal(true);
    }
  }, [location.search]);

  useEffect(() => {
    fetchChallenge(date);
    checkStreakReset();
    loadHowToImages();
    fetchNextChallengeTime();

    const timer = setInterval(() => {
      setNextChallengeSeconds(prev => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timer);
  }, [date, location.pathname, location.search]);

  // Active Timer Effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (gameState === 'playing') {
      lastTickRef.current = Date.now();
      interval = setInterval(() => {
        const now = Date.now();
        const delta = now - lastTickRef.current;
        setElapsedTime(prev => prev + delta);
        lastTickRef.current = now;
      }, 100); // Update every 100ms for smoothness
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [gameState]);

  // Persist state to localStorage
  useEffect(() => {
    if (challenge && gameState !== 'landing') {
      const stateToSave = {
        chain,
        currentArtist,
        currentArtistId,
        currentArtistImg,
        targetArtist,
        targetArtistId,
        targetArtistImg,
        challengeStartArtistImg,
        challengeTargetArtistImg,
        elapsedTime: (hasInteracted || chain.length > 0) ? elapsedTime : 0,
        hasInteracted: hasInteracted || chain.length > 0,
        gameState,
        searchQuery,
        searchResults,
        sessionStartArtist
      };
      // Do not save state for custom challenges
      if (challenge.date !== 'custom') {
        safeStorage.setItem(`song2song_state_${challenge.date}`, JSON.stringify(stateToSave));
      }
    }
  }, [chain, currentArtist, currentArtistId, currentArtistImg, targetArtist, targetArtistId, targetArtistImg, challengeStartArtistImg, challengeTargetArtistImg, elapsedTime, gameState, challenge, searchQuery, searchResults, sessionStartArtist, hasInteracted]);

  // Fetch oracle solution when won
  useEffect(() => {
    if (gameState === 'won' && challenge && !oracleSolution && !isFetchingSolution) {
      fetchOracleSolution();
    }
  }, [gameState, challenge, oracleSolution, isFetchingSolution]);

  // Auto-scroll highlighted search result into view
  useEffect(() => {
    if (gameState === 'playing' && challenge) {
      const hasSeenReverseTip = safeStorage.getItem('song2song_seen_reverse_tip');
      if (!hasSeenReverseTip) {
        const timer = setTimeout(() => {
          triggerToast("Stuck? You can reverse the artist order by clicking the arrows! ⇄");
          safeStorage.setItem('song2song_seen_reverse_tip', 'true');
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [gameState, challenge?.date]);

  useEffect(() => {
    if (highlightedIndex < 0) return;

    const container = window.innerWidth >= 768 ? desktopResultsRef.current : mobileResultsRef.current;
    if (!container) return;

    // The results are inside a div with flex-col, so children are the buttons
    // Desktop has a header div at index 0, so results start at index 1
    // Mobile results are direct children of the scrollable div
    const resultsContainer = window.innerWidth >= 768 
      ? container.querySelector('.flex-col') 
      : container.querySelector('.space-y-2');
    
    if (!resultsContainer) return;

    const highlightedElement = resultsContainer.children[highlightedIndex] as HTMLElement;
    if (highlightedElement) {
      highlightedElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [highlightedIndex]);

  // Track plays from share links
  useEffect(() => {
    if (gameState === 'playing' && challenge) {
      const urlParams = new URLSearchParams(window.location.search);
      const utmSource = urlParams.get('utm_source');
      if (utmSource === 'share' || utmSource === 'listdle') {
        const eventName = utmSource === 'share' ? 'play_from_share' : 'play_from_listdle';
        if (typeof (window as any).sa_event === 'function') {
          (window as any).sa_event(eventName, { date: challenge.date });
        }
        if (typeof (window as any).umami?.track === 'function') {
          (window as any).umami.track(eventName, { date: challenge.date });
        }
      }
    }
  }, [gameState, challenge]);

  const fetchNextChallengeTime = async () => {
    try {
      const res = await fetchWithTimeout('/api/nextChallengeTime');
      const data = await res.json();
      setNextChallengeSeconds(data.secondsRemaining);
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        console.error('Failed to fetch next challenge time', err);
      }
    }
  };

  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const loadHowToImages = async () => {
    try {
      const [savage, malone, swift] = await Promise.all([
        fetchArtistImage('21 Savage'),
        fetchArtistImage('Post Malone'),
        fetchArtistImage('Taylor Swift')
      ]);
      setHowToImages({ 
        savage: savage.imageUrl, 
        malone: malone.imageUrl, 
        swift: swift.imageUrl 
      });
    } catch (err) {
      console.error('Failed to load how-to images', err);
    }
  };

  // Removed auto-scrolling behavior
  // useEffect(() => {
  //   if (chainEndRef.current) {
  //     chainEndRef.current.scrollIntoView({ behavior: 'smooth' });
  //   }
  // }, [chain]);



  const vibrate = (pattern: number | number[] = 10) => {
    if (typeof navigator !== 'undefined' && navigator.vibrate) {
      navigator.vibrate(pattern);
    }
  };

  const fetchChallenge = async (date?: string) => {
    // Basic validation to prevent fetching for favicon.ico or other non-date strings that match the /:date route
    const isCustomPath = date === 'custom' || location.pathname === '/custom' || location.pathname.startsWith('/c/') || !!code;
    if (date && !isCustomPath && !/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      console.warn(`[App] Invalid date parameter: ${date}`);
      setNotFoundMessage("I think you're lost...");
      setIsNotFound(true);
      setLoading(false);
      setGameState('landing'); // Ensure we are in a state that doesn't conflict
      return;
    }

    // Abort any pending fetch
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    try {
      setLoading(true);
      setGameState('landing');
      setError(null);
      setIsNotFound(false);
      setSearchQuery('');
      setSearchResults([]);
      setOracleSolution(null);
      setRevealOraclePath(false);
      setShowVictoryModal(false);
      
      // Reset images to prevent showing previous challenge images while loading
      setCurrentArtistImg('');
      setTargetArtistImg('');
      setChallengeStartArtistImg('');
      setChallengeTargetArtistImg('');
      setCurrentArtistId('');
      setTargetArtistId('');

      if (isCustomPath) {
        let customParam = code || '';
        if (!customParam && location.pathname.startsWith('/c/')) {
          customParam = location.pathname.replace(/^\/c\//, '').split('?')[0];
        }
        if (!customParam) {
          const searchParams = new URLSearchParams(location.search);
          customParam = searchParams.get('c') || searchParams.get('challenge') || '';
        }

        if (customParam) {
          try {
            // Case-sensitive base64 decoding with padding recovery
            let base64 = customParam.trim();
            while (base64.length % 4 !== 0) {
              base64 += '=';
            }
            const decodedPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
                return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));

            let start = '';
            let target = '';

            if (decodedPayload.startsWith('{')) {
              const decoded = JSON.parse(decodedPayload);
              start = (decoded.s || '').trim();
              target = (decoded.t || '').trim();
            } else if (decodedPayload.includes('~')) {
              const parts = decodedPayload.split('~');
              start = (parts[0] || '').trim();
              target = (parts[1] || '').trim();
            }

            if (start && target && start.toLowerCase() !== target.toLowerCase()) {
              const customChallenge = {
                date: 'custom',
                startArtist: start,
                targetArtist: target,
                isCustom: true
              };
              setChallenge(customChallenge);
              setSessionStartArtist(customChallenge.startArtist);
              setTargetArtist(customChallenge.targetArtist);
              setCurrentArtist(customChallenge.startArtist);
              
              setChain([]);
              visitedArtists.current.clear();
              visitedArtists.current.add(normalize(customChallenge.startArtist));
              visitedTracks.current.clear();
              
              const startImgInfo = await fetchArtistImage(customChallenge.startArtist);
              const targetImgInfo = await fetchArtistImage(customChallenge.targetArtist);
              
              if (signal.aborted) return;
              
              setCurrentArtistImg(startImgInfo.imageUrl);
              setCurrentArtistId(startImgInfo.id);
              setTargetArtistImg(targetImgInfo.imageUrl);
              setTargetArtistId(targetImgInfo.id);
              setChallengeStartArtistImg(startImgInfo.imageUrl);
              setChallengeTargetArtistImg(targetImgInfo.imageUrl);
              setElapsedTime(0);
              
              setGameState('playing');
              setLoading(false);
              return;
            }
          } catch (e) {
            console.error("Failed to decode custom challenge", e);
          }
        }
        
        // If we reach here, the custom challenge parameter is invalid, malformed, or missing
        setNotFoundMessage("This custom challenge link is invalid or has expired.");
        setIsNotFound(true);
        setLoading(false);
        setGameState('landing');
        return;
      }
      setTargetArtistId('');

      const url = date ? `/api/challenge/${date}` : '/api/dailyChallenge';
      const res = await fetchWithTimeout(url, { signal });
      if (signal.aborted) return;

      if (!res.ok) {
        if (res.status === 404) {
          const today = new Date().toISOString().split('T')[0];
          if (date && date < '2026-04-03') {
            setNotFoundMessage("What? You thought there would be secret hidden challenges?");
          } else if (date && date > today) {
            setNotFoundMessage("Hey! This challenge isn't out yet, so have some patience!");
          } else {
            setNotFoundMessage("I think you're lost...");
          }
          setIsNotFound(true);
          setLoading(false);
          return;
        }
        let errorMessage = `Server returned ${res.status}`;
        try {
          const errData = await res.json();
          if (errData.error) errorMessage = errData.error;
        } catch (e) {
          // Fallback to generic status message
        }
        throw new Error(errorMessage);
      }
      const data: DailyChallenge = await res.json();
      if (signal.aborted) return;
      
      setChallenge(data);
      recordStart(data.date);
      checkStreakReset();
      
      // Try to load saved state
      let savedStateStr = null;
      try {
        savedStateStr = safeStorage.getItem(`song2song_state_${data.date}`);
      } catch (err) {
        console.error('Failed to access safeStorage for saved state:', err);
      }

      if (savedStateStr) {
        try {
          const savedState = JSON.parse(savedStateStr);
          if (signal.aborted) return;
          
          // Validate saved state against current challenge
          // Allow chains starting with either the start artist or the target artist (for reversed games)
          const isChainValid = savedState.chain?.length === 0 || 
                             normalize(savedState.chain[0].from) === normalize(data.startArtist) ||
                             normalize(savedState.chain[0].from) === normalize(data.targetArtist);
          
          const isCurrentArtistValid = savedState.chain?.length > 0 || 
                                     normalize(savedState.currentArtist) === normalize(data.startArtist) ||
                                     normalize(savedState.currentArtist) === normalize(data.targetArtist);

          // If the target artist has changed, the saved state is invalid
          const isTargetArtistValid = normalize(savedState.targetArtist || '') === normalize(data.targetArtist);

          if (isChainValid && isCurrentArtistValid && isTargetArtistValid) {
            setChain(savedState.chain || []);
            setCurrentArtist(savedState.currentArtist);
            setCurrentArtistId(savedState.currentArtistId || '');
            setSessionStartArtist(savedState.sessionStartArtist || data.startArtist);
            setCurrentArtistImg(savedState.currentArtistImg === '/img/default.png' ? '' : (savedState.currentArtistImg || ''));
            setTargetArtist(data.targetArtist); // Always use server's target artist
            setTargetArtistId(data.targetArtistId || ''); // Always use server's target artist ID
            setTargetArtistImg(savedState.targetArtistImg === '/img/default.png' ? '' : (savedState.targetArtistImg || ''));
            
            // Use saved session images if available, otherwise fall back to current/target images
            const startImg = savedState.challengeStartArtistImg === '/img/default.png' ? '' : (savedState.challengeStartArtistImg || '');
            const tImg = savedState.challengeTargetArtistImg === '/img/default.png' ? '' : (savedState.challengeTargetArtistImg || '');
            
            setChallengeStartArtistImg(startImg || (savedState.chain?.length > 0 ? savedState.chain[0].fromImg : savedState.currentArtistImg) || '');
            setChallengeTargetArtistImg(tImg || savedState.targetArtistImg || '');
            
            // Repair missing or default images
            const currentImgToRepair = savedState.currentArtistImg === '/img/default.png' ? '' : (savedState.currentArtistImg || '');
            const targetImgToRepair = savedState.targetArtistImg === '/img/default.png' ? '' : (savedState.targetArtistImg || '');
            const challengeStartImgToRepair = savedState.challengeStartArtistImg === '/img/default.png' ? '' : (savedState.challengeStartArtistImg || '');
            const challengeTargetImgToRepair = savedState.challengeTargetArtistImg === '/img/default.png' ? '' : (savedState.challengeTargetArtistImg || '');

            const repairPromises = [];

            if (!challengeStartImgToRepair && (savedState.sessionStartArtist || data.startArtist)) {
              repairPromises.push(fetchArtistImage(savedState.sessionStartArtist || data.startArtist).then(info => {
                if (signal.aborted) return;
                setChallengeStartArtistImg(info.imageUrl);
                if (savedState.chain?.length === 0) {
                  setCurrentArtistImg(info.imageUrl);
                }
              }));
            }

            if (!challengeTargetImgToRepair && (savedState.targetArtist || data.targetArtist)) {
              repairPromises.push(fetchArtistImage(savedState.targetArtist || data.targetArtist).then(info => {
                if (signal.aborted) return;
                setChallengeTargetArtistImg(info.imageUrl);
                setTargetArtistImg(info.imageUrl);
              }));
            }
            
            if (!currentImgToRepair && savedState.currentArtist) {
              repairPromises.push(fetchArtistImage(savedState.currentArtist).then(info => {
                if (signal.aborted) return;
                setCurrentArtistImg(info.imageUrl);
                if (!savedState.currentArtistId) setCurrentArtistId(info.id);
              }));
            }

            if (!targetImgToRepair && (savedState.targetArtist || data.targetArtist)) {
              repairPromises.push(fetchArtistImage(savedState.targetArtist || data.targetArtist).then(info => {
                if (signal.aborted) return;
                setTargetArtistImg(info.imageUrl);
                if (!savedState.targetArtistId) setTargetArtistId(info.id);
              }));
            }

            if (repairPromises.length > 0) {
              await Promise.all(repairPromises);
            }
            if (signal.aborted) return;

            setElapsedTime(savedState.elapsedTime || 0);
            setHasInteracted(savedState.hasInteracted || false);
            setSearchQuery(savedState.searchQuery || '');
            setSearchResults(savedState.searchResults || []);
            
            // Rebuild visited sets
            visitedArtists.current.clear();
            visitedTracks.current.clear();
            // Add the session start artist to visited
            const startOfChain = savedState.sessionStartArtist || (savedState.chain?.length > 0 ? savedState.chain[0].from : data.startArtist);
            visitedArtists.current.add(normalize(startOfChain));
            
            (savedState.chain || []).forEach((step: GameStep) => {
              visitedArtists.current.add(normalize(step.to));
              visitedTracks.current.add(step.trackId);
            });
          } else {
            // Stale state detected (e.g. artist was removed from pool), reset for this date
            console.log("Stale state detected for date", data.date, "resetting...");
            safeStorage.removeItem(`song2song_state_${data.date}`);
            setCurrentArtist(data.startArtist);
            setSessionStartArtist(data.startArtist);
            setTargetArtist(data.targetArtist);
            setChain([]);
            visitedArtists.current.clear();
            visitedArtists.current.add(normalize(data.startArtist));
            visitedTracks.current.clear();
            
            const startImg = data.startArtistImg || (await fetchArtistImage(data.startArtist)).imageUrl;
            const targetImg = data.targetArtistImg || (await fetchArtistImage(data.targetArtist)).imageUrl;
            if (signal.aborted) return;

            setCurrentArtistImg(startImg);
            setCurrentArtistId(data.startArtistId || '');
            setTargetArtistImg(targetImg);
            setTargetArtistId(data.targetArtistId || '');
            setChallengeStartArtistImg(startImg);
            setChallengeTargetArtistImg(targetImg);
            setElapsedTime(0);
          }
        } catch (err) {
          console.error('Failed to parse saved state:', err);
          safeStorage.removeItem(`song2song_state_${data.date}`);
          // Fallback to initial state
          setCurrentArtist(data.startArtist);
          setTargetArtist(data.targetArtist);
          setSessionStartArtist(data.startArtist);

          const startImg = data.startArtistImg || (await fetchArtistImage(data.startArtist)).imageUrl;
          const targetImg = data.targetArtistImg || (await fetchArtistImage(data.targetArtist)).imageUrl;
          
          if (signal.aborted) return;
          
          setCurrentArtistImg(startImg);
          setCurrentArtistId(data.startArtistId || '');
          setTargetArtistImg(targetImg);
          setTargetArtistId(data.targetArtistId || '');
          setChallengeStartArtistImg(startImg);
          setChallengeTargetArtistImg(targetImg);
          setElapsedTime(0);
        }
      } else {
        // Initial state
        setCurrentArtist(data.startArtist);
        setSessionStartArtist(data.startArtist);
        setTargetArtist(data.targetArtist);
        setSearchQuery('');
        setSearchResults([]);
        
        visitedArtists.current.clear();
        visitedArtists.current.add(normalize(data.startArtist));
        visitedTracks.current.clear();
        setChain([]);

        const startImg = data.startArtistImg || (await fetchArtistImage(data.startArtist)).imageUrl;
        const targetImg = data.targetArtistImg || (await fetchArtistImage(data.targetArtist)).imageUrl;
        
        if (signal.aborted) return;

        setCurrentArtistImg(startImg);
        setCurrentArtistId(data.startArtistId || '');
        setTargetArtistImg(targetImg);
        setTargetArtistId(data.targetArtistId || '');
        setChallengeStartArtistImg(startImg);
        setChallengeTargetArtistImg(targetImg);
        setElapsedTime(0);
      }

      setLoading(false);
      const isDailyPath = location.pathname === '/daily';
      if (date || isDailyPath) {
        setShowCalendar(false);
        try {
          const savedState = savedStateStr ? JSON.parse(savedStateStr) : null;
          if (savedState?.gameState === 'won' && savedState.chain?.length > 0) {
            setGameState('won');
            setShowVictoryModal(false); // Don't show modal on revisit
          } else {
            setGameState('playing');
          }
        } catch (e) {
          setGameState('playing');
        }
      } else {
        setGameState('landing');
      }
      return data;
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      console.error('Failed to load challenge:', err);
      setError('Failed to load challenge. Please check your connection and try again.');
      setLoading(false);
    }
  };

  const fetchLeaderboardData = async (retryCount = 0) => {
    try {
      const lbRes = await fetchWithTimeout('/api/leaderboard');
      
      if (!lbRes.ok) {
        throw new Error(`Server returned ${lbRes.status}`);
      }

      const lbData = await lbRes.json();
      setLeaderboardData(lbData);
    } catch (err) {
      console.error('Failed to fetch leaderboard data', err);
      // Retry logic for transient network errors
      if (retryCount < 5) {
        setTimeout(() => fetchLeaderboardData(retryCount + 1), 3000);
      }
    }
  };

  const fetchLeaderboard = async () => {
    try {
      setIsFetchingLeaderboard(true);
      await fetchLeaderboardData();
      setShowLeaderboard(true);
    } catch (err) {
      console.error('Failed to fetch leaderboard', err);
    } finally {
      setIsFetchingLeaderboard(false);
    }
  };

  const fetchArtistImage = async (idOrName: string) => {
    try {
      let imageUrl = null;
      let artistId = '';

      // If it looks like a Spotify ID (22 chars alphanumeric), try by ID first
      if (idOrName.length === 22 && /^[a-zA-Z0-9]+$/.test(idOrName)) {
        const res = await fetchWithTimeout(`/api/artist/${idOrName}`);
        if (res.ok) {
          const data = await res.json();
          if (data) {
            const images = data.images || [];
            imageUrl = images.length > 1 ? images[1].url : (images[0]?.url || null);
            artistId = data.id;
          }
        }
      }
      
      // Fallback to name-based search if no imageUrl yet
      if (!imageUrl) {
        const res = await fetchWithTimeout(`/api/spotifyArtistImage?name=${encodeURIComponent(idOrName)}`);
        if (res.ok) {
          const data = await res.json();
          imageUrl = data.imageUrl;
          artistId = data.id;
        }
      }

      // Proxy Spotify images to avoid referrer/blocking issues
      imageUrl = proxySpotifyUrl(imageUrl);

      return { 
        imageUrl: imageUrl || `https://picsum.photos/seed/${encodeURIComponent(idOrName)}/200/200`, 
        id: artistId 
      };
    } catch (err: any) {
      if (err.name !== 'AbortError') {
        console.error('Failed to fetch artist image:', err);
      }
      return { imageUrl: `https://picsum.photos/seed/${encodeURIComponent(idOrName)}/200/200`, id: '' };
    }
  };

  const fetchArtistImageByName = async (name: string) => {
    return fetchArtistImage(name);
  };

  const getHighlightedCurrentArtist = () => {
    const formattedCurrent = formatArtistName(currentArtist);
    if (!formattedCurrent || searchQuery.length < 2) return null;
    
    if (!isShortcut(searchQuery, formattedCurrent, false)) return null;

    const normalizedQuery = normalize(searchQuery);
    const normalizedCurrent = normalize(formattedCurrent);
    
    const index = normalizedCurrent.indexOf(normalizedQuery);
    if (index === -1) {
      // If no exact substring match, maybe a word match
      const qWords = normalizedQuery.split(/\s+/).filter(w => w.length > 2);
      const cWords = normalizedCurrent.split(/\s+/);
      for (const qWord of qWords) {
        if (cWords.includes(qWord)) {
          return <span className="text-[#ff0080] font-bold">"{qWord}" matches {formattedCurrent}</span>;
        }
      }
      return <span className="text-[#ff0080] font-bold">{formattedCurrent}</span>;
    }

    const before = formattedCurrent.substring(0, index);
    const match = formattedCurrent.substring(index, index + normalizedQuery.length);
    const after = formattedCurrent.substring(index + normalizedQuery.length);

    return (
      <span className="text-gray-400">
        {before}
        <span className="text-[#ff0080] font-bold underline underline-offset-4 decoration-2">{match}</span>
        {after}
      </span>
    );
  };

  const getHighlightedTargetArtist = () => {
    const formattedTarget = formatArtistName(targetArtist);
    if (!formattedTarget || searchQuery.length < 2) return null;
    
    if (!isShortcut(searchQuery, formattedTarget, true)) return null;

    const normalizedQuery = normalize(searchQuery);
    const normalizedTarget = normalize(formattedTarget);
    
    const index = normalizedTarget.indexOf(normalizedQuery);
    if (index === -1) {
      // If no exact substring match, maybe a word match
      const qWords = normalizedQuery.split(/\s+/).filter(w => w.length > 2);
      const tWords = normalizedTarget.split(/\s+/);
      for (const qWord of qWords) {
        if (tWords.includes(qWord)) {
          return <span className="text-[#ff0080] font-bold">"{qWord}" matches {formattedTarget}</span>;
        }
      }
      return <span className="text-[#ff0080] font-bold">{formattedTarget}</span>;
    }

    const before = formattedTarget.substring(0, index);
    const match = formattedTarget.substring(index, index + normalizedQuery.length);
    const after = formattedTarget.substring(index + normalizedQuery.length);

    return (
      <span className="text-gray-400">
        {before}
        <span className="text-[#ff0080] font-bold underline underline-offset-4 decoration-2">{match}</span>
        {after}
      </span>
    );
  };

  const triggerToast = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleSearchTrigger = () => {
    if (gameState === 'won') {
      navigate('/');
      return;
    }
    setHasInteracted(true);
    // Lock body scroll immediately to prevent jumping
    if (window.innerWidth < 768) {
      document.body.style.overflow = 'hidden';
      document.body.style.height = '100vh';
    }
    setIsSearchFocused(true);
    // On mobile, we need to focus synchronously to ensure the keyboard opens on iOS
    const input = document.getElementById('mobile-search-input') as HTMLInputElement;
    if (input) {
      input.focus();
    }
  };

  const handleSearch = (query: string) => {
    if (gameState === 'won') return;
    setHasInteracted(true);
    setSearchQuery(query);
    setHighlightedIndex(-1);
    setIsSearchFocused(true);
    
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (analyticsTimeoutRef.current) clearTimeout(analyticsTimeoutRef.current);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Block queries that are too short or only punctuation/whitespace
    const cleanQuery = query.trim().replace(/[^a-z0-9]/gi, '');
    if (cleanQuery.length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    // Analytics Intent Debounce (Slow - 600ms)
    // This ensures we only log the "FINAL" search query, not every intermediate keystroke.
    // We log this even for blocked or cached results to understand what people are trying to find.
    analyticsTimeoutRef.current = setTimeout(() => {
      const queryToTrack = query.trim();
      if (typeof (window as any).sa_event === 'function') {
        (window as any).sa_event('search_performed', { query: queryToTrack, artist: currentArtist });
      }
      if (typeof (window as any).umami?.track === 'function') {
        (window as any).umami.track('search_performed', { query: queryToTrack, artist: currentArtist });
      }
    }, 600);

    const cacheKey = `${normalize(query)}-${normalize(currentArtist)}`;
    if (searchCache.current.has(cacheKey)) {
      setSearchResults(searchCache.current.get(cacheKey)!);
      setIsSearching(false);
      return;
    }

    if (isShortcut(query, targetArtist, true) || isShortcut(query, currentArtist, false)) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }

    // UI Results Debounce (Fast - 150ms)
    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const res = await fetchWithTimeout(`/api/searchTracks?q=${encodeURIComponent(query)}&artist=${encodeURIComponent(currentArtist)}`, {
          signal: controller.signal
        }, 12000);
        if (!res.ok) throw new Error(`Search failed: ${res.status}`);
        const data = await res.json();
        const tracks = (data.tracks?.items || []).filter((track: any) => {
          return !track.artists.some((artist: any) => 
            BLACKLISTED_ARTISTS.some(blacklisted => normalize(artist.name) === normalize(blacklisted))
          );
        });
        setSearchResults(tracks);
        searchCache.current.set(cacheKey, tracks);
      } catch (err: any) {
        if (err.name === 'AbortError') return;
        console.error('Search failed', err);
        setSearchResults([]);
      } finally {
        if (abortControllerRef.current === controller) {
          setIsSearching(false);
          abortControllerRef.current = null;
        }
      }
    }, 150);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (featuredOptions) return;

    if (searchResults.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev + 1) % searchResults.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev - 1 + searchResults.length) % searchResults.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && highlightedIndex < searchResults.length) {
        (async () => {
          const success = await selectTrack(searchResults[highlightedIndex]);
          if (success && window.innerWidth < 768) {
            setIsSearchFocused(false);
          }
        })();
      }
    } else if (e.key === 'Escape') {
      setIsSearchFocused(false);
      setSearchQuery('');
      setSearchResults([]);
    }
  };

  const selectTrack = async (track: Track) => {
    if (gameState === 'won') return false;
    setHasInteracted(true);
    if (visitedTracks.current.has(track.id)) {
      triggerToast("You've already used this song!");
      return false;
    }

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (analyticsTimeoutRef.current) clearTimeout(analyticsTimeoutRef.current);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsSearching(false);

    try {
      const res = await fetchWithTimeout(`/api/trackFeatures?trackId=${track.id}&currentArtist=${encodeURIComponent(currentArtist)}`);
      const data = await res.json();
      const featured = data.featured as { id: string; name: string }[];

      if (!featured || featured.length === 0) {
        triggerToast(`This song only features ${currentArtist}!`);
        return false;
      }

      // Filter out already visited artists
      const validChoices = featured.filter(a => !visitedArtists.current.has(a.id));

      if (validChoices.length === 0) {
        triggerToast("All artists on this track have already been visited!");
        return false;
      }

      if (validChoices.length === 1) {
        addStep(track.name, track.id, validChoices[0].name, validChoices[0].id, !!(track as any).isHint);
      } else {
        setFeaturedOptions({ trackId: track.id, trackName: track.name, artists: validChoices, isHint: !!(track as any).isHint });
      }
      return true;
    } catch (err) {
      console.error('Failed to get track features', err);
      triggerToast("Failed to select track. Please try again.");
      return false;
    }
  };

  const addStep = async (trackName: string, trackId: string, nextArtistName: string, nextArtistId: string, isHint: boolean = false) => {
    setLoading(true);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsSearching(false);

    const nextInfo = await fetchArtistImage(nextArtistId);
    const nextImg = nextInfo.imageUrl;
    
    const newStep: GameStep = {
      from: currentArtist,
      fromId: currentArtistId,
      to: nextArtistName,
      toId: nextArtistId,
      track: trackName,
      fromImg: currentArtistImg,
      toImg: nextImg,
      trackId: trackId,
      isHint: isHint
    };

    visitedTracks.current.add(trackId);
    visitedArtists.current.add(nextArtistId);
    vibrate(20);
    
    // Track move in analytics
    const moveData = { 
      from: currentArtist, 
      to: nextArtistName, 
      track: trackName, 
      isHint: isHint,
      date: challenge?.date 
    };
    if (typeof (window as any).sa_event === 'function') (window as any).sa_event('next_artist', moveData);
    if (typeof (window as any).umami?.track === 'function') (window as any).umami.track('next_artist', moveData);

    setChain([...chain, newStep]);
    setCurrentArtist(nextArtistName);
    setCurrentArtistId(nextArtistId);
    setCurrentArtistImg(nextImg);
    setHints([]);
    setShowHints(false);
    setSearchQuery('');
    setSearchResults([]);
    setIsSearching(false);
    setFeaturedOptions(null);
    setLoading(false);

    const isWin = nextArtistId === targetArtistId || normalize(nextArtistName) === normalize(targetArtist);

    // Auto-focus search input on desktop after a longer delay to beat the onBlur timeout
    if (!isWin) {
      setTimeout(() => {
        if (window.innerWidth >= 768 && searchInputRef.current) {
          searchInputRef.current.focus();
          setIsSearchFocused(true);
        }
      }, 400);
    }

    if (isWin) {
      setGameState('won');
      setIsSearchFocused(false);
      setSearchResults([]);
      setSearchQuery('');
      setShowVictoryModal(true);
      saveStats(chain.length + 1);
    }
  };

  // Reset highlighted artist index when modal opens
  useEffect(() => {
    if (featuredOptions) {
      setHighlightedArtistIndex(0);
    } else {
      setHighlightedArtistIndex(-1);
    }
  }, [featuredOptions]);

  // Handle body scroll lock when search is focused
  useEffect(() => {
    if (isSearchFocused && window.innerWidth < 768) {
      document.body.style.overflow = 'hidden';
      document.body.style.height = '100vh';
    } else {
      document.body.style.overflow = '';
      document.body.style.height = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.height = '';
    };
  }, [isSearchFocused]);

  // Handle global keyboard navigation for featured artist modal
  useEffect(() => {
    if (!featuredOptions) return;
    
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedArtistIndex(prev => (prev + 1) % (featuredOptions.artists.length + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedArtistIndex(prev => (prev - 1 + (featuredOptions.artists.length + 1)) % (featuredOptions.artists.length + 1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (highlightedArtistIndex >= 0 && highlightedArtistIndex < featuredOptions.artists.length) {
          const artist = featuredOptions.artists[highlightedArtistIndex];
          addStep(featuredOptions.trackName, featuredOptions.trackId, artist.name, artist.id);
        } else if (highlightedArtistIndex === featuredOptions.artists.length) {
          setFeaturedOptions(null);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setFeaturedOptions(null);
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [featuredOptions, highlightedArtistIndex, addStep]);

  const fetchOracleSolution = async () => {
    if (!challenge || !currentArtist || !targetArtist) return;
    setIsFetchingSolution(true);
    try {
      const res = await fetchWithTimeout(`/api/solve?start=${encodeURIComponent(sessionStartArtist)}&target=${encodeURIComponent(targetArtist)}`, {}, 15000);
      const data = await res.json();
      if (data.path) {
        setOracleSolution(data);
      }
    } catch (err) {
      console.error('Failed to fetch Oracle solution', err);
    } finally {
      setIsFetchingSolution(false);
    }
  };

  const undoStep = async () => {
    if (chain.length === 0) return;
    setHasInteracted(true);
    vibrate(10);

    const newChain = [...chain];
    const removed = newChain.pop()!;
    
    // Track undo in analytics
    const undoData = { 
      returningTo: removed.from, 
      leaving: removed.to,
      date: challenge?.date 
    };
    if (typeof (window as any).sa_event === 'function') {
      (window as any).sa_event('undo_clicked', undoData);
    }
    if (typeof (window as any).umami?.track === 'function') {
      (window as any).umami.track('undo_clicked', undoData);
    }

    visitedTracks.current.delete(removed.trackId);
    visitedArtists.current.delete(normalize(removed.to));

    setChain(newChain);
    setCurrentArtist(removed.from);
    setCurrentArtistImg(removed.fromImg);
    setHints([]);
    setShowHints(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const submitPostGameFeedback = async () => {
    if (!postGameRating && !postGameFeedback.trim()) return;
    setIsSubmittingPostGameFeedback(true);
    try {
      const type = 'feedback';
      const body = `Rating: ${postGameRating} stars\nFeedback: ${postGameFeedback}`;
      
      await fetchWithTimeout('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: `POST-GAME RATING: ${body}`, type })
      });
      safeStorage.setItem('song2song_feedback_aug2026', 'true');
      setHasSubmittedPostGameFeedback(true);
      triggerToast('Thanks for your feedback!');
    } catch (e) {
      console.error(e);
      triggerToast('Failed to submit, but thanks!');
    } finally {
      setIsSubmittingPostGameFeedback(false);
    }
  };

  const shareResults = async () => {
    if (!challenge) return;
    
    const totalSecs = Math.floor(elapsedTime / 1000);
    const m = Math.floor(totalSecs / 60);
    const s = totalSecs % 60;
    const timeStr = m > 0 ? `${m}m ${s}s` : `${s}s`;
    
    // Calculate challenge number based on 2026-04-03 as #001
    let challengeNum = "???";
    if (challenge.date) {
      try {
        const baseDate = new Date('2026-04-03');
        const challengeDate = new Date(challenge.date);
        const diffTime = challengeDate.getTime() - baseDate.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
        if (diffDays > 0) {
          challengeNum = diffDays.toString().padStart(3, '0');
        }
      } catch (e) {
        console.error("Error calculating challenge number:", e);
      }
    }

    // Create a visual representation of the chain with emojis
    // Format: 👤 🎵 ➔ 🎵 ➔ 🎵 🏆 (one 🎵 per step, 💡 if hint used)
    const emojiChain = '👤 ' + chain.map((step) => step.isHint ? '💡' : '🎵').join(' ➔ ') + ' 🏆';
    
    // Use UTM suffix for tracking
    const shareUrl = `${window.location.origin}/${challenge.date}?utm_source=share`;
    
    // Track the share action itself
    if (typeof (window as any).sa_event === 'function') {
      (window as any).sa_event('share_results', { date: challenge.date, steps: chain.length });
    }
    if (typeof (window as any).umami?.track === 'function') {
      (window as any).umami.track('share_results', { date: challenge.date, steps: chain.length });
    }
    
    const text = `🎵 SongToSong #${challengeNum} 🎵\n\n` +
                 `I connected ${formatArtistName(challenge.startArtist)} to ${formatArtistName(challenge.targetArtist)} in ${chain.length} steps!\n` +
                 `Time: ${timeStr}\n\n` +
                 `${emojiChain}\n\n` +
                 `Can you beat my score?\n` +
                 `Play at: ${shareUrl}`;

    if (navigator.share) {
      try {
        // We omit the 'url' property here because it's already included in the 'text'.
        // This prevents many mobile browsers from pasting the link twice.
        await navigator.share({
          title: 'SongToSong Results',
          text: text,
        });
      } catch (err) {
        // If user cancels or share fails, fallback to clipboard
        if ((err as Error).name !== 'AbortError') {
          copyToClipboard(text);
        }
      }
    } else {
      copyToClipboard(text);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      triggerToast('Results copied to clipboard!');
    }).catch(err => {
      console.error('Failed to copy', err);
      triggerToast('Failed to copy results.');
    });
  };

  const resetGame = () => {
    if (!challenge) return;
    vibrate([10, 50, 10]);
    
    // Track reset in analytics
    if (typeof (window as any).sa_event === 'function') {
      (window as any).sa_event('game_reset', { date: challenge.date });
    }
    if (typeof (window as any).umami?.track === 'function') {
      (window as any).umami.track('game_reset', { date: challenge.date });
    }

    // Reset hints if game was won AND it's not today's daily challenge
    if (gameState === 'won' && challenge?.date) {
      const today = new Date().toISOString().split('T')[0];
      if (challenge.date !== today) {
        const savedHints = safeStorage.getItem('song2songHintsUsed') || '{}';
        const hintsMap = JSON.parse(savedHints);
        delete hintsMap[challenge.date];
        safeStorage.setItem('song2songHintsUsed', JSON.stringify(hintsMap));
        setHintsUsedToday(0);
      }
    }

    setOracleSolution(null);
    setRevealOraclePath(false);
    setShowVictoryModal(false);
    setChain([]);
    setHasInteracted(false);
    setCurrentArtist(challenge.startArtist);
    setSessionStartArtist(challenge.startArtist);
    setTargetArtist(challenge.targetArtist);
    setHints([]);
    setShowHints(false);
    visitedArtists.current.clear();
    visitedArtists.current.add(normalize(challenge.startArtist));
    visitedTracks.current.clear();
    
    setLoading(true);
    Promise.all([
      fetchArtistImage(challenge.startArtist),
      fetchArtistImage(challenge.targetArtist)
    ]).then(([startInfo, targetInfo]) => {
      setCurrentArtistImg(startInfo.imageUrl);
      setCurrentArtistId(startInfo.id);
      setTargetArtistImg(targetInfo.imageUrl);
      setTargetArtistId(targetInfo.id);
      setChallengeStartArtistImg(startInfo.imageUrl);
      setChallengeTargetArtistImg(targetInfo.imageUrl);
      setLoading(false);
    });

    setGameState('playing');
    setElapsedTime(0);
    setSearchQuery('');
    setSearchResults([]);
  };

  const reverseGame = async () => {
    // Track reverse in analytics
    if (typeof (window as any).sa_event === 'function') {
      (window as any).sa_event('game_reversed', { date: challenge.date });
    }
    if (typeof (window as any).umami?.track === 'function') {
      (window as any).umami.track('game_reversed', { date: challenge.date });
    }

    setOracleSolution(null);
    setRevealOraclePath(false);
    setShowVictoryModal(false);
    
    // Toggle between original and reversed
    const isCurrentlyReversed = targetArtist === challenge.startArtist;
    const newStart = isCurrentlyReversed ? challenge.startArtist : challenge.targetArtist;
    const newTarget = isCurrentlyReversed ? challenge.targetArtist : challenge.startArtist;
    
    setChain([]);
    setCurrentArtist(newStart);
    setSessionStartArtist(newStart);
    setTargetArtist(newTarget);
    setHints([]);
    setShowHints(false);
    
    visitedArtists.current.clear();
    visitedArtists.current.add(normalize(newStart));
    visitedTracks.current.clear();

    setLoading(true);
    const [startInfo, targetInfo] = await Promise.all([
      fetchArtistImage(newStart),
      fetchArtistImage(newTarget)
    ]);

    setCurrentArtistImg(startInfo.imageUrl);
    setCurrentArtistId(startInfo.id);
    setTargetArtistImg(targetInfo.imageUrl);
    setTargetArtistId(targetInfo.id);
    setChallengeStartArtistImg(startInfo.imageUrl);
    setChallengeTargetArtistImg(targetInfo.imageUrl);
    setGameState('playing');
    setSearchQuery('');
    setSearchResults([]);
    setLoading(false);
  };

  const getStats = () => {
    const defaultStats = {
      played: 0,
      won: 0,
      streak: 0,
      maxStreak: 0,
      distribution: [0, 0, 0, 0, 0, 0, 0, 0],
      wonDates: [],
      startedDates: []
    };
    try {
      const saved = safeStorage.getItem('song2songStats');
      if (!saved) return defaultStats;
      const stats = JSON.parse(saved);
      return { ...defaultStats, ...stats };
    } catch (err) {
      console.error('Failed to parse stats from safeStorage:', err);
      return defaultStats;
    }
  };

  const checkStreakReset = () => {
    const stats = getStats();
    if (stats.wonDates.length === 0) return;

    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    const todayStr = today.toISOString().split('T')[0];
    
    // Sort won dates to find the most recent one
    const sortedWonDates = [...stats.wonDates].sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
    const lastWonDateStr = sortedWonDates[0];
    const lastWonDate = new Date(lastWonDateStr);
    lastWonDate.setUTCHours(0, 0, 0, 0);

    const diffTime = Math.abs(today.getTime() - lastWonDate.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    // STREAK PARDON: 2026-05-09
    // If today is 2026-05-09, don't reset streaks
    if (todayStr === '2026-05-09') return;
    
    // If today is 2026-05-10, and last win was 2026-05-08, allow the 1-day "pardon" gap
    if (todayStr === '2026-05-10' && lastWonDateStr === '2026-05-08' && diffDays === 2) {
      return;
    }

    // If more than 1 day has passed since the last win, and today isn't the last win day
    if (diffDays > 1) {
      stats.streak = 0;
      safeStorage.setItem('song2songStats', JSON.stringify(stats));
    }
  };

  const recordStart = (date: string) => {
    if (date === 'custom') return;
    const stats = getStats();
    if (!stats.startedDates.includes(date)) {
      stats.startedDates.push(date);
      stats.played++;
      safeStorage.setItem('song2songStats', JSON.stringify(stats));
      if (user) {
        syncCloudStats(user, stats);
      }
    }
  };

  const saveStats = (steps: number) => {
    if (!challenge) return;
    
    // Do not increment global stats or streaks for custom challenges
    if (challenge.date === 'custom') return;

    const stats = getStats();
    const todayStr = new Date().toISOString().split('T')[0];
    
    // Only count unique daily challenges towards "won" and "streak" on first win
    if (!stats.wonDates.includes(challenge.date)) {
      vibrate([50, 100, 50, 100, 50]);
      stats.won++;
      
      // Track completion in analytics
      if (typeof (window as any).sa_event === 'function') {
        (window as any).sa_event('challenge_completed', { 
          date: challenge.date, 
          steps: steps,
          time_seconds: Math.floor(elapsedTime / 1000)
        });
      }
      if (typeof (window as any).umami?.track === 'function') {
        (window as any).umami.track('challenge_completed', { 
          date: challenge.date, 
          steps: steps,
          time_seconds: Math.floor(elapsedTime / 1000)
        });
      }
      
      // Only increment streak if winning today's challenge on the day it came out
      if (challenge.date === todayStr) {
        stats.streak++;
        if (stats.streak > stats.maxStreak) stats.maxStreak = stats.streak;
      }
      
      stats.wonDates.push(challenge.date);
      
      const distIdx = Math.min(steps - 1, 7);
      stats.distribution[distIdx]++;
      
      safeStorage.setItem('song2songStats', JSON.stringify(stats));
      if (user) {
        syncCloudStats(user, stats);
      }
    }
  };

  const fetchHints = async () => {
    if (isLoadingHints || gameState === 'won') return;
    
    // If hints already shown, toggle off
    if (showHints) {
      setShowHints(false);
      return;
    }

    // Check if we already have hints for this artist, just show them
    if (hints.length > 0) {
      setShowHints(true);
      return;
    }

    // Check if this artist is already "unlocked" for this challenge
    let isAlreadyUnlocked = false;
    if (challenge) {
      const savedHints = safeStorage.getItem('song2songHintsUsed') || '{}';
      try {
        const hintsMap = JSON.parse(savedHints);
        const unlocked = hintsMap[challenge.date];
        if (Array.isArray(unlocked) && unlocked.includes(normalize(currentArtist))) {
          isAlreadyUnlocked = true;
        }
      } catch (e) {
        console.error("Error checking unlocked hints:", e);
      }
    }

    // Check limit only if not already unlocked
    if (!isAlreadyUnlocked && hintsUsedToday >= 3) {
      triggerToast("You've used all 3 hints for today!");
      return;
    }

    setIsLoadingHints(true);
    setShowHintTooltip(false);
    safeStorage.setItem('song2songSeenHintTooltip_v3', 'true');
    
    // Dismiss pulse forever
    if (!hintPulseDismissed) {
      setHintPulseDismissed(true);
      safeStorage.setItem('song2songHintPulseDismissed', 'true');
    }

    try {
      const res = await fetch(`/api/hints?artist=${encodeURIComponent(currentArtist)}`);
      if (!res.ok) throw new Error("Failed to fetch hints");
      const data: Track[] = await res.json();
      
      // Filter out tracks that contain the target artist
      const filteredHints = data.filter(track => 
        !track.artists.some(artist => normalize(artist.name) === normalize(targetArtist))
      );
      
      setHints(filteredHints);
      setShowHints(true);
      
      // Increment and save hints used if not already unlocked
      if (challenge && !isAlreadyUnlocked) {
        const savedHints = safeStorage.getItem('song2songHintsUsed') || '{}';
        try {
          const hintsMap = JSON.parse(savedHints);
          let unlocked = hintsMap[challenge.date];
          
          // Migrate old number format to array if needed
          if (!Array.isArray(unlocked)) {
            unlocked = [];
          }
          
          if (!unlocked.includes(normalize(currentArtist))) {
            unlocked.push(normalize(currentArtist));
            hintsMap[challenge.date] = unlocked;
            safeStorage.setItem('song2songHintsUsed', JSON.stringify(hintsMap));
            setHintsUsedToday(unlocked.length);
            
            // Track hint request in analytics
            const eventData = { artist: currentArtist, date: challenge?.date, count: unlocked.length };
            if (typeof (window as any).sa_event === 'function') (window as any).sa_event('hint_requested', eventData);
            if (typeof (window as any).umami?.track === 'function') (window as any).umami.track('hint_requested', eventData);
          }
        } catch (e) {
          console.error("Error saving unlocked hint:", e);
        }
      } else if (isAlreadyUnlocked) {
        // Just track that they viewed an already unlocked hint
        const eventData = { artist: currentArtist, date: challenge?.date, type: 're-view' };
        if (typeof (window as any).sa_event === 'function') (window as any).sa_event('hint_viewed', eventData);
      }

    } catch (err) {
      console.error("Error fetching hints:", err);
      triggerToast("Failed to fetch hints. Try again later.");
    } finally {
      setIsLoadingHints(false);
    }
  };

  const playDailyChallenge = async () => {
    // Track daily play in analytics
    if (typeof (window as any).sa_event === 'function') {
      (window as any).sa_event('daily_played');
    }
    if (typeof (window as any).umami?.track === 'function') {
      (window as any).umami.track('daily_played');
    }
    navigate('/daily');
  };

  const playRandomChallenge = async () => {
    try {
      setLoading(true);
      const res = await fetchWithTimeout('/api/challenges');
      const data = await res.json();
      const dates = Object.keys(data);
      if (dates.length > 0) {
        const randomDate = dates[Math.floor(Math.random() * dates.length)];
        // Track archive play in analytics
        if (typeof (window as any).sa_event === 'function') {
          (window as any).sa_event('archive_played', { date: randomDate });
        }
        if (typeof (window as any).umami?.track === 'function') {
          (window as any).umami.track('archive_played', { date: randomDate });
        }
        navigate(`/${randomDate}`);
      }
    } catch (err) {
      console.error('Failed to fetch challenges for random play', err);
      triggerToast("Failed to pick a random challenge.");
    } finally {
      setLoading(false);
    }
  };

  if (isNotFound) {
    return (
      <>
        <NotFound message={notFoundMessage} isLightMode={isLightMode} />
        {loading && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-[2px]">
            <Loader2 className="w-10 h-10 animate-spin text-[#ff0080]" />
          </div>
        )}
      </>
    );
  }

  return (
    <div className={cn(
      "min-h-[100dvh] font-sans transition-colors duration-300",
      isLightMode 
        ? "bg-[#f5f2ed] text-[#1a1a1a] selection:bg-[#ff0080]/10" 
        : "bg-[#1c1c1c] text-white selection:bg-[#ff0080]/30"
    )}>
      {gameState === 'landing' ? (
        <div className="min-h-[100dvh] flex flex-col items-center justify-start sm:justify-center p-4 pt-[calc(env(safe-area-inset-top,0px)+3rem)] pb-[calc(env(safe-area-inset-bottom,0px)+2rem)] sm:pt-4 text-center relative">
          {/* Top Right User Profile / Auth Button */}
          <div className="absolute top-4 right-4 z-20 flex items-center gap-2">
            <button
              onClick={() => setShowAuthModal(true)}
              className={cn(
                "px-3.5 py-1.5 rounded-full text-xs font-bold flex items-center gap-2 border transition-all shadow-sm",
                isLightMode 
                  ? "bg-white/80 border-black/10 text-[#1a1a1a] hover:bg-white" 
                  : "bg-white/10 border-white/10 text-white hover:bg-white/15"
              )}
            >
              {user ? (
                <>
                  <div className="w-5 h-5 rounded-full bg-[#ff0080] text-white flex items-center justify-center text-[10px] font-bold">
                    {(user.user_metadata?.username?.[0] || user.email?.[0])?.toUpperCase() || <UserCheck className="w-3 h-3" />}
                  </div>
                  <span className="max-w-[120px] truncate">{user.user_metadata?.username || user.email?.split('@')[0]}</span>
                </>
              ) : (
                <>
                  <User className="w-3.5 h-3.5" />
                  <span>Sign In</span>
                </>
              )}
            </button>
          </div>
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md w-full"
          >
            <div className="mb-8 flex justify-center">
              <div className="w-24 h-24 bg-[#ff0080] rounded-full flex items-center justify-center shadow-[0_0_30px_rgba(255,0,128,0.4)]">
                <img src="/doublenote-white.svg" className="w-14 h-14" alt="SongToSong" referrerPolicy="no-referrer" />
              </div>
            </div>
            <h1 
              onClick={toggleLightMode}
              className="flex justify-center mb-6 cursor-pointer select-none"
              title="Click to toggle light/dark theme"
            >
              <img 
                src={isLightMode ? "/songtosong-text-black.svg" : "/songtosong-text-white.svg"} 
                onError={(e) => { e.currentTarget.src = "/songtosong-text-white.svg"; }}
                alt="SongToSong" 
                className={cn(
                  "h-14 sm:h-16 md:h-20 w-auto object-contain max-w-[280px] sm:max-w-xs md:max-w-sm transition-all hover:opacity-90",
                  isLightMode && "brightness-0"
                )}
              />
              <span className="sr-only">SongToSong</span>
            </h1>
            <p className={cn(
              "text-xl mb-8 font-medium",
              isLightMode ? "text-gray-600" : "text-gray-400"
            )}>
              Can you connect the artists through their song collaborations?
            </p>
            
            {error && (
              <div className="mb-8 p-4 bg-red-500/10 border border-red-500/20 rounded-2xl text-red-400 text-sm font-bold">
                {error}
              </div>
            )}
            
            {new Date().toISOString() < '2026-05-21T00:00:00Z' && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={cn(
                  "mb-8 p-4 rounded-2xl border text-sm font-bold flex items-center gap-3 shadow-sm",
                  isLightMode 
                    ? "bg-[#ff0080]/5 border-[#ff0080]/20 text-[#ff0080]" 
                    : "bg-[#ff0080]/10 border-[#ff0080]/20 text-[#ff0080]"
                )}
              >
                <div className="bg-[#ff0080] text-white p-1.5 rounded-lg shrink-0">
                  <Check className="w-4 h-4" />
                </div>
                <p className="text-left">We resolved an issue where certain songs were not appearing in the search results of today's challenge.</p>
              </motion.div>
            )}
            <div className={cn(
              "mb-8 p-4 rounded-2xl border relative overflow-hidden group transition-colors",
              isLightMode ? "bg-white border-black/5" : "bg-white/5 border-white/5"
            )}>
              <div className="relative z-10">
                <p className="text-xs uppercase tracking-widest text-gray-500 font-bold mb-1">Next Challenge In</p>
                <p className="text-2xl font-mono font-bold text-[#ff0080]">{formatTime(nextChallengeSeconds)}</p>
              </div>
            </div>

            <div className="flex flex-col gap-4">
              {getStats().streak > 0 && (
                <div className="flex items-center justify-center gap-2 mb-[-8px]">
                  <span className="text-[10px] font-black text-[#ff0080] uppercase tracking-[0.3em]">
                    {getStats().streak} Day Streak 🔥
                  </span>
                </div>
              )}
              <button 
                onClick={playDailyChallenge}
                className="bg-[#ff0080] text-white py-4 rounded-full font-bold text-xl hover:scale-105 transition-transform shadow-lg flex items-center justify-center gap-2"
              >
                Play Daily Challenge
                {(() => {
                  const today = new Date().toISOString().split('T')[0];
                  const stats = getStats();
                  const wonDates = stats.wonDates || [];
                  if (wonDates.includes(today)) {
                    const savedStateStr = safeStorage.getItem(`song2song_state_${today}`);
                    const savedState = savedStateStr ? JSON.parse(savedStateStr) : null;
                    const isCurrentlyWon = savedState?.gameState === 'won';
                    return <Check className={`w-6 h-6 ${isCurrentlyWon ? 'text-white' : 'text-white/40'}`} />;
                  }
                  return null;
                })()}
              </button>
              
              <button
                onClick={() => setShowCustomChallengeModal(true)}
                className={cn(
                  "py-4 rounded-full font-bold text-xl transition-transform flex items-center justify-center gap-2",
                  isLightMode
                    ? "bg-[#1a1a1a] text-white hover:bg-[#2a2a2a]"
                    : "bg-white text-black hover:bg-gray-200"
                )}
              >
                Create Custom Challenge
              </button>

              <div className="grid grid-cols-2 gap-3">
                <button 
                  onClick={() => setShowCalendar(true)}
                  className={cn(
                    "py-4 rounded-full font-bold text-sm transition-all border",
                    isLightMode 
                      ? "bg-white text-[#1a1a1a] border-black/5 hover:bg-gray-50" 
                      : "bg-white/5 text-white border-white/5 hover:bg-white/10"
                  )}
                >
                  Previous Days
                </button>
                <button 
                  onClick={() => setShowStats(true)}
                  className={cn(
                    "py-4 rounded-full font-bold text-sm transition-all border flex items-center justify-center gap-2",
                    isLightMode 
                      ? "bg-white text-[#1a1a1a] border-black/5 hover:bg-gray-50" 
                      : "bg-white/5 text-white border-white/5 hover:bg-white/10"
                  )}
                >
                  <BarChart2 className="w-4 h-4" />
                  Statistics
                </button>
              </div>

              <button 
                onClick={() => setShowHowTo(true)}
                className={cn(
                  "py-4 rounded-full font-bold text-xl transition-all",
                  isLightMode 
                    ? "bg-[#1a1a1a] text-white hover:bg-black" 
                    : "bg-white/10 text-white hover:bg-white/20"
                )}
              >
                How to Play
              </button>
            </div>

            {/* Hidden SEO Text for Crawlers */}
            <div className="sr-only">
              <h2>SongToSong: The Daily Music Game</h2>
              <p>SongToSong is an addictive daily music puzzle game where players must connect two artists through their song collaborations. Inspired by the logic of "Six Degrees of Kevin Bacon," but for the music world, players navigate a chain of features and guest appearances to find the shortest path between the start and target artist.</p>
              <p>Whether you're a fan of Wordle music games like Heardle, Bandle, or Spotle, SongToSong offers a unique challenge that tests your knowledge of artist connections across genres like Pop, K-Pop (BLACKPINK, BTS, NewJeans), Hip-Hop, and more.</p>
              <p>Key features: Daily challenges updated at midnight, global leaderboards, artist hints, and a competitive "Oracle" mode for music experts. Play the best daily music connection game today and see how few steps it takes you to solve the puzzle!</p>
            </div>

            {/* Minimal Legal Footer */}
            <div className="mt-12 flex flex-col items-center gap-4">
              <div className="flex items-center justify-between w-full text-[10px] font-bold tracking-widest text-gray-600">
                <button onClick={() => setShowTerms(true)} className="hover:text-[#ff0080] transition-colors uppercase">Terms</button>
                <span className="w-1 h-1 rounded-full bg-gray-800" />
                <Link to="/pp" target="_blank" rel="noopener noreferrer" className="hover:text-[#ff0080] transition-colors uppercase">Privacy</Link>
                <span className="w-1 h-1 rounded-full bg-gray-800" />
                <button onClick={() => setShowFeedback(true)} className="hover:text-[#ff0080] transition-colors uppercase">Feedback</button>
                <span className="w-1 h-1 rounded-full bg-gray-800" />
                <a 
                  href="https://ko-fi.com/vonchristie" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="group hover:text-[#ff0080] transition-colors uppercase flex items-center gap-1.5"
                >
                  <img 
                    src={`${import.meta.env.VITE_CDN_URL || 'https://cdn.songtosong.app'}/kofi_symbol.png`}
                    className="w-4 h-4 object-contain grayscale opacity-60 group-hover:grayscale-0 group-hover:opacity-100 group-hover:scale-110 transition-all duration-300" 
                    alt="Ko-fi" 
                    referrerPolicy="no-referrer" 
                  />
                  coffee?
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      ) : (
        <>
          {/* Header */}
          <header className={cn(
            "sticky top-0 z-40 backdrop-blur-md border-b px-4 pt-[calc(env(safe-area-inset-top,0px)+0.75rem)] pb-3 flex items-center justify-between transition-colors",
            isLightMode 
              ? "bg-[#f5f2ed]/80 border-black/5" 
              : "bg-[#1c1c1c]/80 border-white/5"
          )}>
            <div className="flex items-center gap-2.5 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-8 h-8 bg-[#ff0080] rounded-full flex items-center justify-center shrink-0">
                <img src="/doublenote-white.svg" className="w-5 h-5" alt="SongToSong" referrerPolicy="no-referrer" />
              </div>
              <div className="flex items-center gap-2">
                <img 
                  src={isLightMode ? "/songtosong-text-black.svg" : "/songtosong-text-white.svg"} 
                  onError={(e) => { e.currentTarget.src = "/songtosong-text-white.svg"; }}
                  alt="SongToSong" 
                  className={cn("h-6 w-auto object-contain select-none", isLightMode && "brightness-0")}
                />
                <span className="sr-only">SongToSong</span>
                {challenge && getStats().wonDates?.includes(challenge.date) && (
                  <Check className={`w-5 h-5 ${gameState === 'won' ? 'text-[#ff0080]' : 'text-gray-500'}`} />
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setShowHowTo(true)} 
                className={cn(
                  "p-2 rounded-full transition-colors",
                  isLightMode ? "hover:bg-black/5" : "hover:bg-white/5"
                )}
                title="How to Play"
              >
                <Info className="w-5 h-5" />
              </button>
              <button 
                onClick={fetchLeaderboard} 
                className={cn(
                  "p-2 rounded-full transition-colors",
                  isLightMode ? "hover:bg-black/5" : "hover:bg-white/5"
                )}
                title="Leaderboard"
              >
                <Trophy className={cn("w-5 h-5", isLightMode ? "text-[#1a1a1a]" : "text-white")} />
              </button>
              <button 
                onClick={() => setShowStats(true)} 
                className={cn(
                  "p-2 rounded-full transition-colors",
                  isLightMode ? "hover:bg-black/5" : "hover:bg-white/5"
                )}
                title="Statistics"
              >
                <BarChart2 className="w-5 h-5" />
              </button>
              <button 
                onClick={() => setShowAuthModal(true)} 
                className={cn(
                  "p-2 rounded-full transition-colors flex items-center justify-center",
                  isLightMode ? "hover:bg-black/5" : "hover:bg-white/5"
                )}
                title={user ? `Account (${user.user_metadata?.username || user.email})` : "Sign In / Account"}
              >
                {user ? (
                  <div className="w-6 h-6 rounded-full bg-[#ff0080] text-white flex items-center justify-center text-[10px] font-bold shadow-sm">
                    {(user.user_metadata?.username?.[0] || user.email?.[0])?.toUpperCase() || <UserCheck className="w-3.5 h-3.5" />}
                  </div>
                ) : (
                  <User className="w-5 h-5" />
                )}
              </button>
            </div>
          </header>

          <main className="max-w-2xl mx-auto px-4 py-8 pb-64">
            {new Date().toISOString() < '2026-05-21T00:00:00Z' && (
              <motion.div 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={cn(
                  "mb-8 p-4 rounded-2xl border text-sm font-bold flex items-center gap-3 shadow-sm",
                  isLightMode 
                    ? "bg-[#ff0080]/5 border-[#ff0080]/20 text-[#ff0080]" 
                    : "bg-[#ff0080]/10 border-[#ff0080]/20 text-[#ff0080]"
                )}
              >
                <div className="bg-[#ff0080] text-white p-1.5 rounded-lg shrink-0">
                  <Check className="w-4 h-4" />
                </div>
                <p className="text-left">We resolved an issue where certain songs were not appearing in the search results of today's challenge.</p>
              </motion.div>
            )}

            {/* Artist Portraits Row */}
            <div className={cn(
              "flex items-center justify-between mb-12 px-2 py-8 rounded-3xl shadow-xl border transition-colors",
              isLightMode ? "bg-white border-black/5" : "bg-[#2a2a2a] border-white/5"
            )}>
              <div className="flex-1 flex flex-col items-center text-center gap-3 min-w-0">
                <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold">
                  {gameState === 'won' ? 'Start' : 'Current'}
                </p>
                <ArtistImage 
                  src={gameState === 'won' ? challengeStartArtistImg : currentArtistImg} 
                  alt={gameState === 'won' ? sessionStartArtist : currentArtist} 
                  className={cn(
                    "w-24 h-24 rounded-full overflow-hidden border-4 ring-2 shadow-lg flex-shrink-0 transition-colors",
                    isLightMode ? "border-[#f5f2ed] ring-black/5" : "border-[#1c1c1c] ring-white/10"
                  )}
                />
                <p className="font-bold text-lg leading-tight break-words w-full px-2">
                  {formatArtistName(gameState === 'won' ? sessionStartArtist : currentArtist)}
                </p>
              </div>

              {gameState === 'won' ? (
                <div className="p-3 bg-green-500/20 rounded-full border border-green-500/30">
                  <Check className="w-6 h-6 text-green-500" />
                </div>
              ) : (
                <button 
                  onClick={reverseGame}
                  className={cn(
                    "p-3 rounded-full transition-all hover:rotate-180 duration-500 group",
                    isLightMode ? "bg-black/5 hover:bg-black/10" : "bg-white/5 hover:bg-white/10"
                  )}
                  title="Reverse Direction"
                >
                  <ArrowRightLeft className="w-5 h-5 text-[#ff0080] group-hover:scale-110 transition-transform" />
                </button>
              )}

              <div className="flex-1 flex flex-col items-center text-center gap-3 min-w-0">
                <p className="text-[10px] uppercase tracking-[0.2em] text-gray-500 font-bold">Target</p>
                <ArtistImage 
                  src={gameState === 'won' ? challengeTargetArtistImg : targetArtistImg} 
                  alt={targetArtist} 
                  className={cn(
                    "w-24 h-24 rounded-full overflow-hidden border-4 ring-2 shadow-lg flex-shrink-0 transition-colors",
                    isLightMode ? "border-[#f5f2ed] ring-black/5" : "border-[#1c1c1c] ring-white/10"
                  )}
                />
                <p className="font-bold text-lg leading-tight break-words w-full px-2">
                  {formatArtistName(targetArtist)}
                </p>
              </div>
            </div>

            {/* Oracle Solution Section (Visible in main view after winning) */}
            {gameState === 'won' && !showVictoryModal && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-12 flex flex-col gap-6"
              >
                <div className={cn(
                  "border rounded-3xl p-6 text-center transition-colors",
                  isLightMode ? "bg-white border-[#ff0080]/10" : "bg-[#ff0080]/10 border-[#ff0080]/20"
                )}>
                  <div className="flex justify-center mb-4">
                    <Trophy className="w-12 h-12 text-[#ff0080]" />
                  </div>
                  <h2 className={cn("text-2xl font-black mb-1", isLightMode ? "text-[#1a1a1a]" : "text-white")}>Solved!</h2>
                  <p className="text-[#ff0080] font-bold mb-6">
                    Connected in {chain.length} {chain.length === 1 ? 'step' : 'steps'} • {(() => {
                      const totalSecs = Math.floor(elapsedTime / 1000);
                      const m = Math.floor(totalSecs / 60);
                      const s = totalSecs % 60;
                      return m > 0 ? `${m}m ${s}s` : `${s}s`;
                    })()}
                  </p>
                  <div className="flex flex-row gap-3">
                    <button 
                      onClick={shareResults}
                      className="flex-1 bg-[#ff0080] text-white py-4 rounded-full font-black text-sm md:text-lg hover:scale-[1.02] active:scale-[0.98] transition-all shadow-lg flex items-center justify-center gap-2"
                    >
                      <Share2 className="w-4 h-4 md:w-5 md:h-5" /> Share
                    </button>
                    <button 
                      onClick={() => setShowCalendar(true)}
                      className={cn(
                        "flex-1 py-4 rounded-full font-bold text-sm md:text-lg transition-all border",
                        isLightMode 
                          ? "bg-black/5 text-[#1a1a1a] border-black/5 hover:bg-black/10" 
                          : "bg-white/5 text-white border-white/5 hover:bg-white/10"
                      )}
                    >
                      Previous Days
                    </button>
                  </div>
                </div>

                <OracleSolutionSection 
                  oracleSolution={oracleSolution}
                  isFetchingSolution={isFetchingSolution}
                  revealOraclePath={revealOraclePath}
                  setRevealOraclePath={setRevealOraclePath}
                  isLightMode={isLightMode}
                  challenge={challenge}
                />
              </motion.div>
            )}

            {/* Desktop Search Input (Hidden on Mobile) */}
            <div className="hidden md:block relative mb-12">
              <div className="relative group">
                <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 group-focus-within:text-[#ff0080] transition-colors" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onFocus={() => {
                    setIsSearchFocused(true);
                    setHasInteracted(true);
                  }}
                  onBlur={() => setTimeout(() => setIsSearchFocused(false), 200)}
                  disabled={gameState === 'won' || !!featuredOptions || loading}
                  placeholder={gameState === 'won' ? "Game Over! Press \"Replay?\" to play again." : featuredOptions ? "Select an artist below..." : loading ? "Loading next artist..." : isSearching ? "Searching..." : `Enter a song by/featuring ${formatArtistName(currentArtist)}...`}
                  className={cn(
                    "w-full border-none rounded-2xl py-5 pl-14 pr-24 focus:ring-2 focus:ring-[#ff0080] transition-all outline-none text-lg shadow-inner disabled:opacity-50 disabled:cursor-not-allowed",
                    isLightMode ? "bg-white text-[#1a1a1a]" : "bg-[#2a2a2a] text-white"
                  )}
                />
                <div className="absolute right-5 top-1/2 -translate-y-1/2 flex items-center gap-2">
                  {searchQuery ? (
                    <button 
                      onClick={() => { setSearchQuery(''); setSearchResults([]); }}
                      className="p-1 hover:bg-white/10 rounded-full transition-colors"
                    >
                      <X className="w-5 h-5 text-gray-500" />
                    </button>
                  ) : (
                    <div className="relative">
                      <button 
                        onClick={fetchHints}
                        disabled={isLoadingHints || gameState === 'won' || loading}
                        className={cn(
                          "p-2 rounded-xl transition-all flex items-center gap-2 group",
                          showHints 
                            ? "bg-[#ff0080] text-white" 
                            : isLightMode 
                              ? "bg-black/5 text-gray-500 hover:bg-black/10 hover:text-[#1a1a1a]" 
                              : "bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white"
                        )}
                        title="Get a hint"
                      >
                        {isLoadingHints ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <Lightbulb className={cn("w-5 h-5", showHints ? "fill-white" : "group-hover:fill-white/20")} />
                            {!showHints && hintsUsedToday === 0 && !hintPulseDismissed && gameState !== 'won' && (
                              <span className="absolute -top-1 -right-1 flex h-3 w-3">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#ff0080] opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-3 w-3 bg-[#ff0080]"></span>
                              </span>
                            )}
                          </>
                        )}
                      </button>
                      
                      <AnimatePresence>
                        {showHintTooltip && !showHints && (
                          <motion.div 
                            initial={{ opacity: 0, y: 10, scale: 0.9 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            className="absolute bottom-full right-0 mb-4 w-52 bg-[#ff0080] text-white p-3 rounded-2xl shadow-2xl z-[60]"
                          >
                            <div className="relative">
                              <div className="flex items-start gap-2">
                                <p className="text-[11px] font-bold leading-tight flex-1">Tap the lightbulb for a hint! (3 per day)</p>
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowHintTooltip(false);
                                    safeStorage.setItem('song2songSeenHintTooltip_v3', 'true');
                                    
                                    // Dismiss pulse forever if they close the tooltip
                                    if (!hintPulseDismissed) {
                                      setHintPulseDismissed(true);
                                      safeStorage.setItem('song2songHintPulseDismissed', 'true');
                                    }
                                  }}
                                  className="p-1 hover:bg-white/10 rounded-full shrink-0 pointer-events-auto"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                              <div className="absolute top-full right-4 w-3 h-3 bg-[#ff0080] rotate-45 -translate-y-1/2" />
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}
                </div>
              </div>

              {/* Hints Row (Desktop) */}
              <AnimatePresence>
                {showHints && hints.length > 0 && !searchQuery && gameState !== 'won' && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-4 flex flex-col gap-2">
                      <div className="flex items-center justify-between px-1">
                        <div className="flex items-center gap-2">
                          <Sparkles className="w-3 h-3 text-[#ff0080]" />
                          <span className="text-[10px] uppercase tracking-widest font-black text-gray-500">Popular Collaborations</span>
                        </div>
                        <span className="text-[10px] font-bold text-gray-600">
                          {3 - hintsUsedToday} {3 - hintsUsedToday === 1 ? 'hint' : 'hints'} left {!date ? 'today' : ''}
                        </span>
                      </div>
                      <div className="flex gap-3 overflow-x-auto pb-2 no-scrollbar">
                        {hints.map((track) => (
                          <motion.button
                            key={track.id}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => {
                              selectTrack({ ...track, isHint: true });
                              // Track hint selection in analytics
                              const eventData = { track: track.name, artist: currentArtist, date: challenge?.date };
                              if (typeof (window as any).sa_event === 'function') (window as any).sa_event('hint_selected', eventData);
                              if (typeof (window as any).umami?.track === 'function') (window as any).umami.track('hint_selected', eventData);
                            }}
                            className="flex-shrink-0 w-56 bg-[#2a2a2a] hover:bg-[#333] border border-white/5 rounded-2xl p-3 flex items-center gap-3 transition-all group text-left shadow-lg"
                          >
                            <TrackImage track={track} className="w-12 h-12 rounded-xl" />
                            <div className="min-w-0 flex-1">
                              <p className={cn(
                                "text-xs font-bold truncate transition-colors",
                                isLightMode ? "text-[#1a1a1a] group-hover:text-[#ff0080]" : "text-white group-hover:text-[#ff0080]"
                              )}>
                                {track.name}
                              </p>
                              <p className="text-[10px] text-gray-500 truncate">
                                {track.artists.filter(a => normalize(a.name) !== normalize(currentArtist)).map(a => formatArtistName(a.name)).join(', ')}
                              </p>
                            </div>
                          </motion.button>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Search Results Dropdown (Desktop) */}
              <AnimatePresence>
                {isSearchFocused && gameState !== 'won' && (
                  <motion.div 
                    ref={desktopResultsRef}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    onMouseLeave={() => setHighlightedIndex(-1)}
                    className={cn(
                      "absolute top-full left-0 right-0 mt-3 backdrop-blur-xl rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] border overflow-hidden z-50 max-h-[400px] overflow-y-auto transition-colors",
                      isLightMode ? "bg-white/95 border-black/5" : "bg-[#2a2a2a]/95 border-white/10"
                    )}
                  >
                    {!isSearching && searchQuery.length < 2 && searchResults.length === 0 ? (
                      <div className="p-12 flex flex-col items-center justify-center text-center gap-6">
                        <Music className={cn("w-10 h-10 opacity-20", isLightMode ? "text-black" : "text-white")} />
                        <div className="space-y-4">
                          <p className={cn("text-sm transition-colors max-w-[240px] mx-auto", isLightMode ? "text-gray-500" : "text-gray-400")}>
                            Enter at least 2 characters to search for songs by {formatArtistName(currentArtist)}.
                          </p>
                          <p className={cn("text-[10px] uppercase tracking-[0.2em] font-bold transition-colors", isLightMode ? "text-gray-500" : "text-gray-600")}>
                            {3 - hintsUsedToday} {3 - hintsUsedToday === 1 ? 'HINT' : 'HINTS'} REMAINING TODAY
                          </p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className={cn(
                          "p-3 border-b flex items-center justify-between",
                          isLightMode ? "bg-black/5 border-black/5" : "bg-white/5 border-white/5"
                        )}>
                          <span className="text-[10px] uppercase tracking-widest font-black text-gray-500">Search Results</span>
                          {isSearching ? (
                            <Loader2 className="w-3 h-3 animate-spin text-[#ff0080]" />
                          ) : (
                            <span className="text-[10px] text-gray-600 font-bold">{searchResults.length} found</span>
                          )}
                        </div>
                        
                        {isSearching ? (
                          <div className="p-12 flex flex-col items-center justify-center gap-4">
                            <Loader2 className="w-8 h-8 animate-spin text-[#ff0080]" />
                            <p className="text-xs text-gray-500">Searching...</p>
                          </div>
                        ) : searchResults.length > 0 ? (
                          <div className="p-3 flex flex-col p-2 space-y-1">
                            {searchResults.map((track, index) => (
                              <button
                                key={track.id}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={async () => {
                                  const success = await selectTrack(track);
                                  if (success) {
                                    setIsSearchFocused(false);
                                  }
                                }}
                                onMouseMove={() => {
                                  if (highlightedIndex !== index) setHighlightedIndex(index);
                                }}
                                className={cn(
                                  "w-full p-3 flex items-center gap-4 rounded-xl transition-all group text-left",
                                  highlightedIndex === index ? "bg-[#ff0080]/20" : isLightMode ? "hover:bg-black/5" : "hover:bg-[#ff0080]/10"
                                )}
                              >
                                <TrackImage track={track} className={cn("transition-transform", highlightedIndex === index && "scale-105")} />
                                <div className="flex-1 min-w-0">
                                  <p className={cn(
                                    "font-bold truncate transition-colors",
                                    highlightedIndex === index ? "text-[#ff0080]" : isLightMode ? "text-[#1a1a1a] group-hover:text-[#ff0080]" : "text-white group-hover:text-[#ff0080]"
                                  )}>
                                    {track.name}
                                  </p>
                                  <p className="text-xs text-gray-500 truncate">
                                    {track.artists.map(a => formatArtistName(a.name)).join(', ')}
                                  </p>
                                </div>
                                <ChevronRight className={cn(
                                  "w-5 h-5 transition-all text-[#ff0080] translate-x-1",
                                  !(highlightedIndex === index) && (isLightMode ? "text-gray-300 group-hover:text-[#ff0080] group-hover:translate-x-1" : "text-gray-700 group-hover:text-[#ff0080] group-hover:translate-x-1")
                                )} />
                              </button>
                            ))}
                          </div>
                        ) : searchQuery.length >= 2 && (
                          <div className="p-12 text-center">
                            <p className="text-gray-500 text-sm">No songs found for "{searchQuery}"</p>
                            {getHighlightedTargetArtist() || getHighlightedCurrentArtist() ? (
                              <div className="mt-2 flex flex-col gap-1 items-center">
                                {getHighlightedTargetArtist() && (
                                  <p className="text-[10px] text-gray-600 italic">
                                    Note: You cannot search for the target artist's name: {getHighlightedTargetArtist()}
                                  </p>
                                )}
                                {getHighlightedCurrentArtist() && (
                                  <p className="text-[10px] text-gray-600 italic">
                                    Note: You cannot search for the current artist's name: {getHighlightedCurrentArtist()}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <p className="text-[10px] text-gray-600 mt-2 italic">Note: You cannot search for the artist's name.</p>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Mobile Search Trigger (Only on Mobile) */}
            <div className="md:hidden mb-12">
              <button 
                onClick={handleSearchTrigger}
                className={cn(
                  "w-full rounded-2xl py-5 px-6 flex items-center gap-4 text-lg shadow-inner border active:scale-[0.98] transition-all",
                  isLightMode 
                    ? "bg-white text-gray-500 border-black/5" 
                    : "bg-[#2a2a2a] text-gray-500 border-white/5"
                )}
              >
                <Search className="w-6 h-6" />
                <span>{gameState === 'won' ? "Game over! Play again?" : "Search for a song..."}</span>
              </button>
            </div>

        {/* Chain Display */}
        <div className="space-y-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xs uppercase tracking-[0.2em] text-gray-500 font-bold">Chain So Far</h3>
            <span className="text-xs font-bold text-[#ff0080] bg-[#ff0080]/10 px-3 py-1 rounded-full">
              {chain.length} {chain.length === 1 ? 'Connection' : 'Connections'}
            </span>
          </div>
          
          <div className="space-y-3">
            {chain.length === 0 && (
              <div className={cn(
                "text-center py-12 px-10 border-2 border-dashed rounded-3xl italic text-sm transition-colors",
                isLightMode ? "border-black/5 text-gray-400" : "border-white/5 text-gray-600"
              )}>
                Start your journey by searching for a song...
              </div>
            )}
            <AnimatePresence mode="popLayout">
              {[...chain].reverse().map((step, index) => (
                <motion.div 
                  key={`${step.trackId}-${step.to}`}
                  layout
                  initial={{ opacity: 0, scale: 0.95, y: -10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 10 }}
                  transition={{ 
                    type: 'spring', 
                    damping: 20, 
                    stiffness: 300,
                    layout: { duration: 0.2 }
                  }}
                  className={cn(
                    "p-4 rounded-2xl flex items-center gap-4 border shadow-md transition-colors",
                    isLightMode ? "bg-white border-black/5" : "bg-[#2a2a2a] border-white/5"
                  )}
                >
                  <div className="flex -space-x-3 isolate">
                    <ArtistImage 
                      src={step.fromImg} 
                      alt={step.from} 
                      className={cn(
                        "w-10 h-10 rounded-full border-2 z-20 transition-colors",
                        isLightMode ? "border-white" : "border-[#2a2a2a]"
                      )} 
                    />
                    <ArtistImage 
                      src={step.toImg} 
                      alt={step.to} 
                      className={cn(
                        "w-10 h-10 rounded-full border-2 z-10 transition-colors",
                        isLightMode ? "border-white" : "border-[#2a2a2a]"
                      )} 
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500 font-medium">{formatArtistName(step.from)} → {formatArtistName(step.to)}</p>
                    <p className={cn("font-bold truncate", isLightMode ? "text-[#1a1a1a]" : "text-white")}>{step.track}</p>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
            <div ref={chainEndRef} />
          </div>
        </div>

        {/* Action Buttons */}
        <div className={cn(
          "fixed bottom-0 left-0 right-0 backdrop-blur-lg border-t p-4 z-40 transition-colors",
          isLightMode ? "bg-[#f5f2ed]/90 border-black/5" : "bg-[#1c1c1c]/90 border-white/10"
        )}>
          <div className="max-w-2xl mx-auto flex gap-4">
            <button 
              onClick={undoStep}
              disabled={chain.length === 0 || normalize(currentArtist) === normalize(targetArtist)}
              className={cn(
                "flex-1 py-4 rounded-2xl font-bold flex items-center justify-center gap-2 transition-all border disabled:opacity-30 disabled:cursor-not-allowed",
                isLightMode 
                  ? "bg-white text-[#1a1a1a] border-black/5 hover:bg-gray-50" 
                  : "bg-white/5 text-white border-white/5 hover:bg-white/10"
              )}
            >
              <Undo2 className="w-5 h-5" /> Go Back
            </button>
            <button 
              onClick={resetGame}
              disabled={chain.length === 0 && gameState !== 'won'}
              className={cn(
                "flex-1 py-4 rounded-2xl font-bold flex items-center justify-center gap-2 transition-all border disabled:opacity-30 disabled:cursor-not-allowed",
                isLightMode 
                  ? "bg-red-500/10 hover:bg-red-500/20 text-red-600 border-red-500/10" 
                  : "bg-red-500/10 hover:bg-red-500/20 text-red-400 border-red-500/10"
              )}
            >
              <RotateCcw className="w-5 h-5" /> {(normalize(currentArtist) === normalize(targetArtist) || gameState === 'won') ? 'Replay?' : 'Reset'}
            </button>
          </div>
        </div>
      </main>
        </>
      )}

      {/* Full-Screen Mobile Search Overlay */}
      <AnimatePresence>
        {isSearchFocused && gameState !== 'won' && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={cn(
              "fixed inset-0 z-[100] md:hidden flex flex-col transition-colors",
              isLightMode ? "bg-[#f5f2ed]" : "bg-[#1c1c1c]"
            )}
          >
            {/* Mobile Search Header */}
            <div className={cn(
              "p-4 border-b flex items-center gap-3",
              isLightMode ? "border-black/5" : "border-white/5"
            )}>
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input 
                  autoFocus
                  type="text"
                  id="mobile-search-input"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={!!featuredOptions}
                  placeholder={featuredOptions ? "Select an artist..." : isSearching ? "Searching..." : `Search for ${formatArtistName(currentArtist)}...`}
                  className={cn(
                    "w-full border-none rounded-xl py-3 pl-12 pr-20 outline-none focus:ring-2 focus:ring-[#ff0080] disabled:opacity-50 transition-colors",
                    isLightMode ? "bg-white text-[#1a1a1a]" : "bg-[#2a2a2a] text-white"
                  )}
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  {searchQuery ? (
                    <button 
                      onClick={() => { setSearchQuery(''); setSearchResults([]); }}
                      className="p-1.5"
                    >
                      <X className="w-4 h-4 text-gray-500" />
                    </button>
                  ) : (
                    <button 
                      onClick={fetchHints}
                      disabled={isLoadingHints || gameState === 'won' || loading}
                      className={cn(
                        "p-2 rounded-lg transition-all relative",
                        showHints ? "bg-[#ff0080] text-white" : isLightMode ? "text-gray-500" : "text-gray-400"
                      )}
                    >
                      {isLoadingHints ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Lightbulb className={cn("w-4 h-4", showHints && "fill-white")} />
                          {!showHints && hintsUsedToday === 0 && !hintPulseDismissed && gameState !== 'won' && (
                            <span className="absolute top-1 right-1 flex h-2 w-2">
                              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#ff0080] opacity-75"></span>
                              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#ff0080]"></span>
                            </span>
                          )}
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
              <button 
                onClick={() => { setIsSearchFocused(false); setSearchResults([]); setSearchQuery(''); }}
                className="text-sm font-bold text-[#ff0080] px-2"
              >
                Cancel
              </button>
            </div>

            {/* Mobile Search Results */}
            <div 
              ref={mobileResultsRef} 
              onMouseLeave={() => setHighlightedIndex(-1)}
              className="flex-1 overflow-y-auto p-4"
            >
              {/* Mobile Hints */}
              <AnimatePresence>
                {showHints && hints.length > 0 && !searchQuery && gameState !== 'won' && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="mb-6"
                  >
                    <div className="flex items-center justify-between mb-3 px-1">
                      <div className="flex items-center gap-2">
                        <Sparkles className="w-3 h-3 text-[#ff0080]" />
                        <span className="text-[10px] uppercase tracking-widest font-black text-gray-500">Popular Collaborations</span>
                      </div>
                      <span className="text-[10px] font-bold text-gray-600">{3 - hintsUsedToday} left</span>
                    </div>
                    <div className="flex gap-3 overflow-x-auto pb-2 no-scrollbar">
                      {hints.map((track) => (
                        <button
                          key={track.id}
                          onClick={() => {
                            selectTrack({ ...track, isHint: true });
                            setIsSearchFocused(false);
                            // Track hint selection in analytics
                            const eventData = { track: track.name, artist: currentArtist, date: challenge?.date };
                            if (typeof (window as any).sa_event === 'function') (window as any).sa_event('hint_selected', eventData);
                            if (typeof (window as any).umami?.track === 'function') (window as any).umami.track('hint_selected', eventData);
                          }}
                          className={cn(
                            "flex-shrink-0 w-44 border rounded-xl p-3 flex flex-col gap-2 text-left transition-colors",
                            isLightMode ? "bg-white border-black/5" : "bg-[#2a2a2a] border-white/5"
                          )}
                        >
                          <TrackImage track={track} className="w-full aspect-square h-auto rounded-lg mb-1" />
                          <div className="min-w-0">
                            <p className={cn("text-[11px] font-bold truncate", isLightMode ? "text-[#1a1a1a]" : "text-white")}>{track.name}</p>
                            <p className="text-[9px] text-gray-500 truncate">
                              {track.artists.filter(a => normalize(a.name) !== normalize(currentArtist)).map(a => formatArtistName(a.name)).join(', ')}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Mobile Search Results List */}
              {isSearching ? (
                <div className="py-20 flex flex-col items-center gap-4">
                  <Loader2 className="w-8 h-8 animate-spin text-[#ff0080]" />
                  <p className="text-gray-500 text-sm">Searching...</p>
                </div>
              ) : searchResults.length > 0 ? (
                <div className="space-y-2">
                  {searchResults.map((track) => (
                    <button
                      key={track.id}
                      onClick={async () => {
                        const success = await selectTrack(track);
                        if (success) {
                          setIsSearchFocused(false);
                        }
                      }}
                      className={cn(
                        "w-full p-4 rounded-2xl flex items-center gap-4 border transition-colors",
                        isLightMode ? "bg-white border-black/5" : "bg-white/5 border-white/5"
                      )}
                    >
                      <TrackImage track={track} className="w-14 h-14 rounded-xl shadow-lg" />
                      <div className="flex-1 min-w-0 text-left">
                        <p className={cn("font-bold truncate", isLightMode ? "text-[#1a1a1a]" : "text-white")}>{track.name}</p>
                        <p className="text-sm text-gray-500 truncate">{track.artists.map(a => formatArtistName(a.name)).join(', ')}</p>
                      </div>
                      <ChevronRight className="w-6 h-6 text-gray-700" />
                    </button>
                  ))}
                </div>
              ) : (!searchQuery || searchQuery.length < 2) && !showHints ? (
                <div className="py-24 flex flex-col items-center justify-center text-center px-10 gap-6">
                  <Music className={cn("w-10 h-10 opacity-20", isLightMode ? "text-black" : "text-white")} />
                  <div className="space-y-4">
                    <p className={cn("text-sm transition-colors max-w-[240px] mx-auto", isLightMode ? "text-gray-500" : "text-gray-400")}>
                      Enter at least 2 characters to search for songs by {formatArtistName(currentArtist)}.
                    </p>
                    <p className={cn("text-[10px] uppercase tracking-[0.2em] font-bold transition-colors", isLightMode ? "text-gray-500" : "text-gray-600")}>
                      {3 - hintsUsedToday} {3 - hintsUsedToday === 1 ? 'HINT' : 'HINTS'} REMAINING TODAY
                    </p>
                  </div>
                </div>
              ) : searchQuery.length >= 2 && (
                <div className="py-20 text-center">
                  <p className="text-gray-500">No songs found for "{searchQuery}"</p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Featured Artist Selection Modal */}
      <AnimatePresence>
        {featuredOptions && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setFeaturedOptions(null)}
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              onMouseLeave={() => setHighlightedArtistIndex(-1)}
              className={cn(
                "relative w-full max-w-sm rounded-3xl p-6 border shadow-2xl overflow-y-auto max-h-[90vh] transition-colors",
                isLightMode ? "bg-white border-black/5" : "bg-[#2a2a2a] border-white/10"
              )}
            >
              <h2 className={cn("text-lg font-bold mb-1", isLightMode ? "text-[#1a1a1a]" : "text-white")}>Select Featured Artist</h2>
              <p className={cn("text-xs mb-4", isLightMode ? "text-gray-500" : "text-gray-400")}>Which artist from <span className={cn("font-bold", isLightMode ? "text-[#1a1a1a]" : "text-white")}>"{featuredOptions.trackName}"</span> do you want to follow?</p>
              
              <div className="grid grid-cols-1 gap-2">
                {featuredOptions.artists.map((artist, idx) => (
                  <button
                    key={`${artist.id}-${idx}`}
                    onClick={() => addStep(featuredOptions.trackName, featuredOptions.trackId, artist.name, artist.id, !!featuredOptions.isHint)}
                    onMouseMove={() => {
                      if (highlightedArtistIndex !== idx) setHighlightedArtistIndex(idx);
                    }}
                    className={cn(
                      "py-3 rounded-xl font-bold transition-all text-left px-4 flex items-center justify-between group text-sm",
                      highlightedArtistIndex === idx 
                        ? "bg-[#ff0080] text-white shadow-lg" 
                        : isLightMode 
                          ? "bg-black/5 hover:bg-[#ff0080]/20 text-[#1a1a1a]/80 hover:text-[#ff0080]" 
                          : "bg-white/5 hover:bg-[#ff0080]/20 text-white/80 hover:text-white"
                    )}
                  >
                    {formatArtistName(artist.name)}
                    <ChevronRight className={cn(
                      "w-4 h-4 transition-opacity",
                      highlightedArtistIndex === idx ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                    )} />
                  </button>
                ))}
              </div>
              
              <button 
                onClick={() => setFeaturedOptions(null)}
                onMouseMove={() => {
                  if (highlightedArtistIndex !== featuredOptions.artists.length) {
                    setHighlightedArtistIndex(featuredOptions.artists.length);
                  }
                }}
                className={cn(
                  "w-full mt-4 py-2 rounded-xl text-xs font-bold transition-all",
                  highlightedArtistIndex === featuredOptions.artists.length
                    ? "bg-white/10 text-white"
                    : "text-gray-500 hover:text-white"
                )}
              >
                Cancel
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Victory Modal */}
      <AnimatePresence>
        {showVictoryModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowVictoryModal(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              className={cn(
                "relative z-10 border rounded-3xl p-8 text-center max-w-sm w-full shadow-[0_0_50px_rgba(255,0,128,0.2)] overflow-y-auto max-h-[90vh] transition-colors",
                isLightMode 
                  ? "bg-white border-[#ff0080]/20 text-[#1a1a1a]" 
                  : "bg-[#2a2a2a] border-[#ff0080]/30 text-white"
              )}
            >
              <button 
                onClick={() => setShowVictoryModal(false)}
                className={cn(
                  "absolute top-4 right-4 p-2 rounded-full transition-colors",
                  isLightMode ? "hover:bg-black/5" : "hover:bg-white/5"
                )}
              >
                <X className="w-5 h-5" />
              </button>
              <div className="mb-6 flex justify-center">
                <motion.div 
                  animate={{ rotate: [0, 10, -10, 0], scale: [1, 1.1, 1] }}
                  transition={{ repeat: Infinity, duration: 2 }}
                >
                  <Trophy className="w-16 h-16 text-[#ff0080]" />
                </motion.div>
              </div>
              <h2 className="text-4xl font-black mb-2 tracking-tighter">Solved!</h2>
              <div className="space-y-1 mb-8">
                <p className="text-lg font-bold text-[#ff0080]">
                  Connected in {chain.length} {chain.length === 1 ? 'step' : 'steps'}
                </p>
                {chain.filter(s => s.isHint).length > 0 && (
                  <p className="text-sm font-bold text-[#ff0080]/80">
                    {chain.filter(s => s.isHint).length} {chain.filter(s => s.isHint).length === 1 ? 'hint' : 'hints'} used
                  </p>
                )}
                <p className={cn("text-sm", isLightMode ? "text-gray-500" : "text-gray-400")}>
                  Time: {(() => {
                    const totalSecs = Math.floor(elapsedTime / 1000);
                    const m = Math.floor(totalSecs / 60);
                    const s = totalSecs % 60;
                    return m > 0 ? `${m}m ${s}s` : `${s}s`;
                  })()}
                </p>
              </div>

              {/* Oracle Solution Section */}
              <OracleSolutionSection 
                oracleSolution={oracleSolution}
                isFetchingSolution={isFetchingSolution}
                revealOraclePath={revealOraclePath}
                setRevealOraclePath={setRevealOraclePath}
                isModal={true}
                isLightMode={isLightMode}
                challenge={challenge}
              />
              
              {['2026-08-28', '2026-08-29', '2026-08-30'].includes(challenge?.date || '') && !hasSubmittedPostGameFeedback && (
                <div className={cn("mb-8 p-4 rounded-2xl border text-left", isLightMode ? "bg-[#ece9e4] border-black/5" : "bg-black/30 border-white/5")}>
                  <h3 className="text-sm font-bold mb-3 text-center">Rate your experience</h3>
                  <div className="flex justify-center gap-2 mb-4">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => setPostGameRating(star)}
                        className="p-1 transition-transform hover:scale-110 active:scale-95"
                      >
                        <Star className={cn(
                          "w-8 h-8 transition-colors",
                          postGameRating >= star ? "fill-[#ff0080] text-[#ff0080]" : "text-gray-400"
                        )} />
                      </button>
                    ))}
                  </div>
                  <textarea
                    className={cn(
                      "w-full mb-3 px-3 py-3 rounded-xl border text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#ff0080]",
                      isLightMode ? "bg-white border-black/10" : "bg-[#1c1c1c] border-white/10"
                    )}
                    rows={3}
                    placeholder="What's going well? What could improve?"
                    value={postGameFeedback}
                    onChange={(e) => setPostGameFeedback(e.target.value)}
                  />
                  <button
                    onClick={submitPostGameFeedback}
                    disabled={isSubmittingPostGameFeedback || (!postGameRating && !postGameFeedback.trim())}
                    className="w-full bg-[#ff0080] text-white py-3 rounded-xl font-bold text-sm hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:hover:scale-100"
                  >
                    {isSubmittingPostGameFeedback ? 'Submitting...' : 'Submit Feedback'}
                  </button>
                </div>
              )}

              <div className="flex flex-col gap-3">
                <button 
                  onClick={shareResults}
                  className="bg-[#ff0080] text-white py-4 rounded-full font-black text-lg hover:scale-105 transition-transform shadow-lg flex items-center justify-center gap-2"
                >
                  <Share2 className="w-5 h-5" /> Share Results
                </button>
                <button 
                  onClick={resetGame}
                  className={cn(
                    "py-4 rounded-full font-bold text-lg transition-all border",
                    isLightMode 
                      ? "bg-[#1a1a1a] text-white border-black/5 hover:bg-black" 
                      : "bg-white/5 text-white border-white/5 hover:bg-white/10"
                  )}
                >
                  Replay?
                </button>
                <button 
                  onClick={() => setShowCalendar(true)}
                  className={cn(
                    "py-4 rounded-full font-bold text-lg transition-all border",
                    isLightMode 
                      ? "bg-black/5 text-[#1a1a1a] border-black/5 hover:bg-black/10" 
                      : "bg-white/5 text-white border-white/5 hover:bg-white/10"
                  )}
                >
                  Play Previous Days
                </button>
                <button 
                  onClick={() => setShowVictoryModal(false)}
                  className="text-gray-500 text-sm font-bold hover:text-[#ff0080] transition-colors pt-2"
                >
                  Review Chain
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Leaderboard & Recent Connections Modal */}
      <AnimatePresence>
        {showLeaderboard && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowLeaderboard(false)}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className={cn(
                "relative w-full max-w-2xl rounded-3xl p-8 border shadow-2xl overflow-hidden flex flex-col max-h-[90vh] transition-colors",
                isLightMode ? "bg-[#f5f2ed] border-black/5" : "bg-[#1a1a1a] border-white/10"
              )}
            >
              <button onClick={() => setShowLeaderboard(false)} className={cn(
                "absolute top-6 right-6 p-2 rounded-full z-10 transition-colors",
                isLightMode ? "hover:bg-black/5" : "hover:bg-white/5"
              )}>
                <X className="w-5 h-5" />
              </button>
              
              <div className="flex items-center gap-3 mb-8">
                <div className="p-3 bg-[#ff0080]/10 rounded-2xl">
                  <Trophy className={cn("w-6 h-6", isLightMode ? "text-[#ff0080]" : "text-white")} />
                </div>
                <div>
                  <h2 className={cn("text-2xl font-black", isLightMode ? "text-[#1a1a1a]" : "text-white")}>Oracle Insights</h2>
                  <p className="text-xs text-gray-500 font-bold uppercase tracking-widest">Global Leaderboard</p>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto pr-2 space-y-12 custom-scrollbar">
                {/* Leaderboard Section */}
                <section>
                  <h3 className={cn("text-sm font-bold mb-6 flex items-center gap-2", isLightMode ? "text-[#1a1a1a]" : "text-white")}>
                    <Trophy className={cn("w-4 h-4", isLightMode ? "text-[#ff0080]" : "text-white")} />
                    Most Connected Artists
                  </h3>
                  <div className="grid gap-2">
                    {leaderboardData.map((artist, i) => (
                      <div key={`${artist.name}-${i}`} className={cn(
                        "flex items-center justify-between p-4 rounded-2xl group transition-colors shadow-sm",
                        isLightMode ? "bg-white hover:bg-gray-50" : "bg-white/5 hover:bg-white/10"
                      )}>
                        <div className="flex items-center gap-4">
                          <span className={cn(
                            "w-6 text-center font-black text-xs",
                            i === 0 ? "text-yellow-500" : i === 1 ? "text-gray-400" : i === 2 ? "text-amber-600" : "text-gray-600"
                          )}>
                            {i + 1}
                          </span>
                          <span className={cn("font-bold group-hover:text-[#ff0080] transition-colors", isLightMode ? "text-[#1a1a1a]" : "text-white")}>{formatArtistName(artist.name)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={cn("text-xl font-black", isLightMode ? "text-[#1a1a1a]" : "text-white")}>{artist.count}</span>
                          <span className="text-[10px] text-gray-500 font-bold uppercase">Artists</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* How To Play Modal */}
      <AnimatePresence>
        {showHowTo && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowHowTo(false)}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className={cn(
                "relative w-full max-w-md rounded-3xl p-8 border shadow-2xl overflow-y-auto max-h-[90vh] transition-colors",
                isLightMode ? "bg-[#f5f2ed] border-black/5" : "bg-[#2a2a2a] border-white/10"
              )}
            >
              <button 
                onClick={() => setShowHowTo(false)} 
                className={cn(
                  "absolute top-6 right-6 p-2 rounded-full transition-colors",
                  isLightMode ? "hover:bg-black/5" : "hover:bg-white/5"
                )}
              >
                <X className="w-5 h-5" />
              </button>
              <h2 className={cn("text-2xl font-bold mb-6 text-center", isLightMode ? "text-[#1a1a1a]" : "text-white")}>How to Play</h2>
              <div className={cn("space-y-6 text-center transition-colors", isLightMode ? "text-gray-600" : "text-gray-300")}>
                <p className="text-sm leading-relaxed">
                  Find the path from the starting artist to the target artist by selecting songs that feature another artist, chaining them until you reach your goal!
                </p>
                
                <p className="text-sm">
                  For example, to connect 21 Savage to Taylor Swift, you could do this:
                </p>

                <div className="flex flex-col items-center gap-3 py-2">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-10 h-10 rounded-full overflow-hidden ring-2 transition-colors",
                      isLightMode ? "bg-gray-200 ring-black/5" : "bg-gray-700 ring-white/10"
                    )}>
                      <img src={howToImages.savage} alt="21 Savage" className="w-full h-full object-cover" />
                    </div>
                    <span className={cn("font-bold", isLightMode ? "text-[#1a1a1a]" : "text-white")}>21 Savage</span>
                  </div>
                  
                  <div className="text-gray-600"><ArrowRightLeft className="w-4 h-4 rotate-90" /></div>
                  
                  <div className={cn("text-sm transition-colors", isLightMode ? "text-gray-500" : "text-gray-400")}>
                    Post Malone - rockstar (feat. 21 Savage)
                  </div>
                  
                  <div className={cn("transition-colors", isLightMode ? "text-[#ff0080]/60" : "text-gray-600")}><ArrowRightLeft className="w-4 h-4 rotate-90" /></div>
                  
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-10 h-10 rounded-full overflow-hidden ring-2 transition-colors",
                      isLightMode ? "bg-gray-200 ring-black/5" : "bg-gray-700 ring-white/10"
                    )}>
                      <img src={howToImages.malone} alt="Post Malone" className="w-full h-full object-cover" />
                    </div>
                    <span className={cn("font-bold", isLightMode ? "text-[#1a1a1a]" : "text-white")}>Post Malone</span>
                  </div>
                  
                  <div className={cn("transition-colors", isLightMode ? "text-[#ff0080]/60" : "text-gray-600")}><ArrowRightLeft className="w-4 h-4 rotate-90" /></div>
                  
                  <div className={cn("text-sm transition-colors", isLightMode ? "text-gray-500" : "text-gray-400")}>
                    Taylor Swift - Fortnight (feat. Post Malone)
                  </div>
                  
                  <div className={cn("transition-colors", isLightMode ? "text-[#ff0080]/60" : "text-gray-600")}><ArrowRightLeft className="w-4 h-4 rotate-90" /></div>
                  
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-10 h-10 rounded-full overflow-hidden ring-2 transition-colors",
                      isLightMode ? "bg-gray-200 ring-black/5" : "bg-gray-700 ring-white/10"
                    )}>
                      <img src={howToImages.swift} alt="Taylor Swift" className="w-full h-full object-cover" />
                    </div>
                    <span className={cn("font-bold", isLightMode ? "text-[#1a1a1a]" : "text-white")}>Taylor Swift</span>
                  </div>
                </div>

                <p className="text-sm border-t border-black/5 pt-6">
                  This chain represents a valid connection between these artists!
                </p>
                
                <p className={cn("text-sm font-bold", isLightMode ? "text-[#1a1a1a]" : "text-white")}>
                  Good luck and have fun!
                </p>

                <div className={cn("pt-4 border-t", isLightMode ? "border-black/5" : "border-white/5")}>
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Lightbulb className="w-4 h-4 text-[#ff0080] fill-[#ff0080]/20" />
                    <h3 className={cn("text-sm font-bold", isLightMode ? "text-[#1a1a1a]" : "text-white")}>Hints</h3>
                  </div>
                  <p className={cn("text-xs transition-colors", isLightMode ? "text-gray-500" : "text-gray-400")}>
                    Stuck? Tap the lightbulb icon in the search bar to see popular collaborations for the current artist. You get <span className={cn("font-bold", isLightMode ? "text-[#1a1a1a]" : "text-white")}>3 hints per day</span>, so use them wisely!
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setShowHowTo(false)}
                className="w-full mt-8 bg-[#ff0080] text-white py-4 rounded-full font-bold hover:scale-105 transition-transform"
              >
                Got it!
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Stats Modal */}
      <AnimatePresence>
        {showStats && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowStats(false)}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className={cn(
                "relative w-full max-w-md rounded-3xl p-8 border shadow-2xl overflow-y-auto max-h-[90vh] transition-colors",
                isLightMode ? "bg-[#f5f2ed] border-black/5" : "bg-[#2a2a2a] border-white/10"
              )}
            >
              <button 
                onClick={() => setShowStats(false)} 
                className={cn(
                  "absolute top-6 right-6 p-2 rounded-full transition-colors",
                  isLightMode ? "hover:bg-black/5" : "hover:bg-white/5"
                )}
              >
                <X className="w-5 h-5" />
              </button>
              
              <div className="flex items-center gap-3 mb-8">
                <div className="p-3 bg-[#ff0080]/10 rounded-2xl">
                  <BarChart2 className="w-6 h-6 text-[#ff0080]" />
                </div>
                <div>
                  <h2 className={cn("text-2xl font-bold", isLightMode ? "text-[#1a1a1a]" : "text-white")}>Statistics</h2>
                  <p className="text-xs text-gray-500 font-bold uppercase tracking-widest">Your Progress</p>
                </div>
              </div>
              
              {(() => {
                const stats = getStats();
                const cardClass = cn(
                  "p-5 rounded-2xl text-center shadow-sm border transition-colors",
                  isLightMode ? "bg-white border-black/5" : "bg-[#1c1c1c] border-white/5"
                );
                return (
                  <>
                    <div className="grid grid-cols-2 gap-4 mb-12">
                      <div className={cardClass}>
                        <p className="text-3xl font-black text-[#ff0080]">{stats.played}</p>
                        <p className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Played</p>
                      </div>
                      <div className={cardClass}>
                        <p className="text-3xl font-black text-[#ff0080]">{stats.played ? Math.round((stats.won / stats.played) * 100) : 0}%</p>
                        <p className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Win %</p>
                      </div>
                      <div className={cardClass}>
                        <p className="text-3xl font-black text-[#ff0080]">{stats.streak}</p>
                        <p className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Streak</p>
                      </div>
                      <div className={cardClass}>
                        <p className="text-3xl font-black text-[#ff0080]">{stats.maxStreak}</p>
                        <p className="text-[10px] text-gray-500 uppercase tracking-widest font-bold">Max Streak</p>
                      </div>
                    </div>

                    <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-gray-500 mb-4">Guess Distribution</h3>
                    <div className="space-y-3">
                      {stats.distribution.map((count: number, i: number) => {
                        const max = Math.max(...stats.distribution) || 1;
                        const width = (count / max) * 100;
                        return (
                          <div key={i} className="flex items-center gap-3">
                            <span className="text-xs font-bold w-8 text-right text-gray-500">{i === 7 ? '8+' : i + 1}</span>
                            <div className="flex-1 h-6 bg-white/5 rounded-sm overflow-hidden relative">
                              {count > 0 && (
                                <motion.div 
                                  initial={{ width: 0 }}
                                  animate={{ width: `${Math.max(width, 5)}%` }}
                                  className="h-full bg-[#ff0080] flex items-center justify-end px-2 text-[10px] font-bold text-white"
                                >
                                  {count}
                                </motion.div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()}

              <div className="flex flex-col gap-3 mt-10">
                <button 
                  onClick={() => setShowStats(false)}
                  className="w-full bg-white text-black py-4 rounded-full font-bold hover:scale-105 transition-transform"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Toast Notification */}
      <AnimatePresence>
        {showToast && (
          <motion.div
            initial={{ opacity: 0, y: -20, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, y: -20, x: '-50%' }}
            className={cn(
              "fixed top-24 left-1/2 z-[100] px-5 py-4 rounded-2xl font-bold shadow-[0_20px_50px_rgba(0,0,0,0.5)] border flex items-center gap-3 max-w-[90vw] w-max min-w-[280px] text-center justify-center transition-colors",
              isLightMode 
                ? "bg-white/95 text-[#1a1a1a] border-black/5" 
                : "bg-[#1a1a1a]/95 text-white border-white/10"
            )}
          >
            <div className="w-8 h-8 rounded-full bg-[#ff0080]/20 flex items-center justify-center flex-shrink-0">
              <Info className="w-4 h-4 text-[#ff0080]" />
            </div>
            <span className="text-sm md:text-base leading-tight">{toastMessage}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Calendar / Previous Days Modal */}
      <AnimatePresence>
        {showCustomChallengeModal && (
          <CustomChallengeModal
            onClose={() => setShowCustomChallengeModal(false)}
            isLightMode={isLightMode}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAuthModal && (
          <AuthModal
            onClose={() => setShowAuthModal(false)}
            isLightMode={isLightMode}
            user={user}
            onUserChange={setUser}
            getStats={getStats}
            onStatsUpdated={(updated) => safeStorage.setItem('song2songStats', JSON.stringify(updated))}
          />
        )}
      </AnimatePresence>

      <UpdateNoticeModal 
        isOpen={showUpdateNotice}
        isLightMode={isLightMode}
        onClose={() => {
          safeStorage.setItem('song2song_update_notice_seen', 'true');
          setShowUpdateNotice(false);
        }}
      />

      <ChallengeNoticeModal 
        isOpen={showChallengeNotice}
        isLightMode={isLightMode}
        onClose={() => {
          safeStorage.setItem('song2song_challenge_notice_20260907_seen', 'true');
          setShowChallengeNotice(false);
        }}
      />

      <AnimatePresence>
        {showCalendar && (
          <CalendarModal 
            onClose={() => setShowCalendar(false)} 
            isLightMode={isLightMode} 
            getStats={getStats} 
          />
        )}
      </AnimatePresence>

      {/* Loading Overlay */}
      <AnimatePresence>
        {loading && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-[2px]">
            <Loader2 className="w-10 h-10 animate-spin text-[#ff0080]" />
          </div>
        )}
      </AnimatePresence>

      {/* Terms of Service Modal */}
      <AnimatePresence>
        {showTerms && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowTerms(false)}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className={cn(
                "relative w-full max-w-md rounded-3xl p-8 border shadow-2xl overflow-y-auto max-h-[90vh] transition-colors",
                isLightMode ? "bg-[#f5f2ed] border-black/5" : "bg-[#2a2a2a] border-white/10"
              )}
            >
              <button 
                onClick={() => setShowTerms(false)} 
                className={cn(
                  "absolute top-6 right-6 p-2 rounded-full transition-colors",
                  isLightMode ? "hover:bg-black/5" : "hover:bg-white/5"
                )}
              >
                <X className="w-5 h-5" />
              </button>
              <h2 className={cn("text-2xl font-bold mb-6", isLightMode ? "text-[#1a1a1a]" : "text-white")}>Terms of Use</h2>
              <div className={cn("space-y-4 text-sm leading-relaxed transition-colors", isLightMode ? "text-gray-600" : "text-gray-400")}>
                <p>
                  By using SongToSong, you agree to use the service for personal, non-commercial purposes only.
                </p>
                <p>
                  All music metadata, artist names, and imagery are provided by Spotify and belong to their respective rightsholders. We do not claim ownership of any third-party content.
                </p>
                <p>
                  SongToSong is provided "as is" without warranties of any kind. We are not responsible for any inaccuracies in the data provided by Spotify or our internal pathfinding systems.
                </p>
                <p>
                  We reserve the right to modify or discontinue the service at any time without notice.
                </p>
              </div>
              <button 
                onClick={() => setShowTerms(false)}
                className={cn(
                  "w-full mt-8 py-4 rounded-full font-bold hover:scale-105 transition-transform",
                  isLightMode ? "bg-[#1a1a1a] text-white" : "bg-white text-black"
                )}
              >
                Close
              </button>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Feedback Modal */}
      <AnimatePresence>
        {showFeedback && (
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => !isSubmittingFeedback && setShowFeedback(false)}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className={cn(
                "relative w-full max-w-md rounded-3xl p-8 border shadow-2xl transition-colors",
                isLightMode ? "bg-[#f5f2ed] border-black/5" : "bg-[#2a2a2a] border-white/10"
              )}
            >
              <button 
                onClick={() => setShowFeedback(false)} 
                disabled={isSubmittingFeedback}
                className={cn(
                  "absolute top-6 right-6 p-2 rounded-full transition-colors disabled:opacity-50",
                  isLightMode ? "hover:bg-black/5" : "hover:bg-white/5"
                )}
              >
                <X className="w-5 h-5" />
              </button>
              
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-[#ff0080]/10 rounded-2xl">
                  <MessageSquare className="w-6 h-6 text-[#ff0080]" />
                </div>
                <div>
                  <h2 className={cn("text-2xl font-bold", isLightMode ? "text-[#1a1a1a]" : "text-white")}>Feedback</h2>
                  <p className="text-xs text-gray-500 font-bold uppercase tracking-widest">Help us improve</p>
                </div>
              </div>

              <div className="space-y-4">
                <textarea
                  value={feedbackMessage}
                  onChange={(e) => setFeedbackMessage(e.target.value)}
                  disabled={isSubmittingFeedback}
                  placeholder="Found a bug? Have a suggestion? Or just want to say hi? Type it here..."
                  className={cn(
                    "w-full h-40 p-5 rounded-2xl border outline-none focus:ring-2 focus:ring-[#ff0080] transition-all resize-none text-sm leading-relaxed",
                    isLightMode 
                      ? "bg-white border-black/5 text-[#1a1a1a]" 
                      : "bg-[#1c1c1c] border-white/10 text-white"
                  )}
                />

                <p className={cn("text-[11px] leading-relaxed transition-colors", isLightMode ? "text-gray-500" : "text-gray-400")}>
                  You can also email us at <a href="mailto:contact@songtosong.com" className="text-[#ff0080] hover:underline font-bold">contact@songtosong.com</a> or DM <a href="https://twitter.com/vonchristie_" target="_blank" rel="noopener noreferrer" className="text-[#ff0080] hover:underline font-bold">@vonchristie_</a>
                </p>

                <div className="flex gap-3 pt-2">
                  <button 
                    onClick={() => setShowFeedback(false)}
                    disabled={isSubmittingFeedback}
                    className={cn(
                      "flex-1 py-4 rounded-full font-bold transition-all disabled:opacity-50",
                      isLightMode ? "bg-black/5 text-[#1a1a1a]" : "bg-white/5 text-white"
                    )}
                  >
                    Cancel
                  </button>
                  <button 
                    disabled={!feedbackMessage.trim() || isSubmittingFeedback}
                    onClick={async () => {
                      setIsSubmittingFeedback(true);
                      try {
                        const res = await fetch('/api/feedback', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ message: feedbackMessage })
                        });
                        if (res.ok) {
                          triggerToast("Thank you for your feedback!");
                          setFeedbackMessage('');
                          setShowFeedback(false);
                        } else {
                          throw new Error("Failed to send");
                        }
                      } catch (err) {
                        triggerToast("Failed to send feedback. Try again later.");
                      } finally {
                        setIsSubmittingFeedback(false);
                      }
                    }}
                    className={cn(
                      "flex-[2] bg-[#ff0080] text-white py-4 rounded-full font-bold shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:scale-100"
                    )}
                  >
                    {isSubmittingFeedback ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Send className="w-4 h-4" /> Send Feedback
                      </>
                    )}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isLightMode = safeStorage.getItem('song2songLightMode') === 'true';

  useEffect(() => {
    fetch('/api/admin/stats')
      .then(res => res.json())
      .then(data => {
        if (data.error) throw new Error(data.message || data.error);
        setStats(data.data);
        setIsLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setIsLoading(false);
      });
  }, []);

  if (isLoading) {
    return (
      <div className={cn("min-h-screen flex items-center justify-center", isLightMode ? "bg-[#f5f2ed]" : "bg-[#121212]")}>
        <Loader2 className="w-10 h-10 animate-spin text-[#ff0080]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn("min-h-screen flex flex-col items-center justify-center p-6 text-center", isLightMode ? "bg-[#f5f2ed]" : "bg-[#121212]")}>
        <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mb-6">
          <X className="w-8 h-8 text-red-500" />
        </div>
        <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
        <p className="text-gray-500 mb-8 max-w-sm">
          {error.includes("credentials") 
            ? "UMAMI_USERNAME and UMAMI_PASSWORD are not set in the environment variables." 
            : error}
        </p>
        <button onClick={() => navigate('/')} className="px-6 py-2 bg-[#ff0080] text-white rounded-full font-bold">Return Home</button>
      </div>
    );
  }

  return (
    <div className={cn("min-h-screen p-6", isLightMode ? "bg-[#f5f2ed] text-[#1a1a1a]" : "bg-[#121212] text-white")}>
      <div className="max-w-4xl mx-auto">
        <header className="flex items-center justify-between mb-12">
          <div>
            <h1 className="text-3xl font-black tracking-tighter">Admin Dashboard</h1>
            <p className="text-gray-500 uppercase text-[10px] tracking-widest font-bold">Last 24 Hours (Internal)</p>
          </div>
          <button onClick={() => navigate('/')} className="p-2 hover:bg-black/5 rounded-full transition-colors"><X className="w-6 h-6" /></button>
        </header>

        {/* Top Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Views', value: stats.summary.pageviews.value, color: 'text-blue-500' },
            { label: 'Visitors', value: stats.summary.visitors.value, color: 'text-green-500' },
            { label: 'Bounces', value: stats.summary.bounces.value, color: 'text-red-500' },
            { label: 'Avg Time', value: `${Math.round(stats.summary.totaltime.value / stats.summary.pageviews.value)}s`, color: 'text-orange-500' },
          ].map(stat => (
            <div key={stat.label} className={cn("p-6 rounded-3xl border", isLightMode ? "bg-white border-black/5" : "bg-white/5 border-white/5")}>
              <p className="text-[10px] uppercase font-bold text-gray-500 mb-1">{stat.label}</p>
              <p className={cn("text-2xl font-black", stat.color)}>{stat.value}</p>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Top Referrers */}
          <div className={cn("p-8 rounded-[40px] border", isLightMode ? "bg-white border-black/5" : "bg-white/5 border-white/5")}>
            <div className="flex items-center gap-2 mb-6">
              <Users className="w-5 h-5 text-[#ff0080]" />
              <h2 className="text-lg font-bold">Top Referrers</h2>
            </div>
            <div className="space-y-4">
              {stats.referrers.map((ref: any, idx: number) => (
                <div key={idx} className="flex items-center justify-between group">
                  <span className="text-sm font-medium text-gray-400 capitalize">{ref.x || 'Direct'}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-black">{ref.y}</span>
                    <div className="w-24 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                      <motion.div 
                        initial={{ width: 0 }}
                        animate={{ width: `${(ref.y / stats.referrers[0].y) * 100}%` }}
                        className="h-full bg-[#ff0080]"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Pages/Artists */}
          <div className={cn("p-8 rounded-[40px] border", isLightMode ? "bg-white border-black/5" : "bg-white/5 border-white/5")}>
            <div className="flex items-center gap-2 mb-6">
              <Music className="w-5 h-5 text-blue-500" />
              <h2 className="text-lg font-bold">Top URLs</h2>
            </div>
            <div className="space-y-4">
              {stats.pages.slice(0, 10).map((page: any, idx: number) => (
                <div key={idx} className="flex items-center justify-between">
                  <span className="text-xs font-mono text-gray-500 truncate mr-4 max-w-[200px]">{page.x}</span>
                  <span className="text-sm font-black">{page.y}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SurveyPage() {
  const [surveyOption, setSurveyOption] = useState<string>('');
  const [surveyCustomText, setSurveyCustomText] = useState<string>('');
  const [isSubmittingSurvey, setIsSubmittingSurvey] = useState(false);
  const navigate = useNavigate();

  const isLightMode = safeStorage.getItem('song2song_lightmode') === 'true';

  const submitSurvey = async () => {
    if (!surveyOption && !surveyCustomText.trim()) return;
    setIsSubmittingSurvey(true);
    try {
      const type = 'survey';
      const body = surveyOption === 'Other (write in)' ? surveyCustomText : surveyOption;

      if (typeof (window as any).umami?.track === 'function') {
        const safeName = surveyOption === 'Other (write in)' ? 'other' : surveyOption.split(' ')[0].toLowerCase().replace(/[^a-z]/g, '');
        (window as any).umami.track(`survey_vote_${safeName}`, { option: body });
        (window as any).umami.track('survey_response', { option: body });
      }

      await fetchWithTimeout('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message: `SURVEY RESPONSE: ${body}`, type })
      });
      safeStorage.setItem('song2song_survey_completed', 'true');
      alert('Thanks for your input!');
      navigate('/');
    } catch (e) {
      console.error(e);
      alert('Failed to submit, but thanks!');
    } finally {
      setIsSubmittingSurvey(false);
    }
  };

  return (
    <div className={cn("min-h-screen text-center p-6 flex flex-col items-center justify-center", isLightMode ? "bg-[#f5f2eb] text-[#1a1a1a]" : "bg-[#121212] text-white")}>
      <div className="max-w-md w-full">
        <h1 className="text-3xl font-black mb-8 uppercase tracking-widest text-[#ff0080]">SongToSong Survey</h1>
        
        {safeStorage.getItem('song2song_survey_completed') === 'true' && !window.location.search.includes('previewSurvey=true') ? (
          <div>
            <Check className="w-16 h-16 text-[#ff0080] mx-auto mb-4" />
            <p className="text-xl font-bold mb-8">You've already completed the survey!</p>
            <button 
              onClick={() => navigate('/')}
              className="bg-black/10 hover:bg-black/20 text-current px-6 py-3 rounded-full font-bold transition-colors"
            >
              Back to Game
            </button>
          </div>
        ) : (
          <div className={cn("mb-8 p-6 rounded-3xl border text-left", isLightMode ? "bg-white border-black/5 shadow-sm" : "bg-[#1c1c1c] border-white/5")}>
            <h3 className="text-lg font-bold mb-6 text-center">What would you like to see come to SongToSong?</h3>
            <div className="space-y-4 mb-6">
              {['User accounts and leaderboards', 'A blitz mode (max. connections in 60s)', 'Custom user-made challenges', 'Other (write in)'].map((opt) => (
                <label key={opt} className={cn(
                  "flex items-center gap-3 text-base p-4 rounded-xl cursor-pointer hover:opacity-80 transition-all",
                  isLightMode ? "bg-[#f5f2eb]" : "bg-black/30",
                  surveyOption === opt && "ring-2 ring-[#ff0080]"
                )}>
                  <input
                    type="radio"
                    name="surveyOptionPage"
                    value={opt}
                    checked={surveyOption === opt}
                    onChange={(e) => setSurveyOption(e.target.value)}
                    className="accent-[#ff0080] w-5 h-5"
                  />
                  {opt}
                </label>
              ))}
            </div>
            {surveyOption === 'Other (write in)' && (
              <textarea
                className={cn(
                  "w-full mb-6 p-4 rounded-xl border text-base outline-none resize-none min-h-[100px]",
                  isLightMode ? "bg-white border-black/10 focus:border-[#ff0080]" : "bg-[#121212] border-white/10 focus:border-[#ff0080]"
                )}
                placeholder="Tell us what you want..."
                value={surveyCustomText}
                onChange={(e) => setSurveyCustomText(e.target.value)}
              />
            )}
            <button
              onClick={submitSurvey}
              disabled={isSubmittingSurvey || (!surveyOption && !surveyCustomText)}
              className="w-full bg-[#ff0080] text-white py-4 rounded-xl font-bold text-lg hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:hover:scale-100"
            >
              {isSubmittingSurvey ? 'Submitting...' : 'Submit feedback'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<GameContent />} />
        <Route path="/daily" element={<GameContent />} />
        <Route path="/c/:code" element={<GameContent />} />
        <Route path="/custom" element={<GameContent />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/tos" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/pp" element={<PrivacyPolicy />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/survey" element={<SurveyPage />} />
        <Route path="/:date" element={<GameContent />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}
